import { __extends } from "tslib";
import { ColorUtils, Matrix, Rectangle, Point, Vector3D } from '@awayjs/core';
import { ImageSampler, Float2Attributes } from '@awayjs/stage';
import { Style, TriangleElements } from '@awayjs/renderer';
import { Graphics, Shape, GraphicsFactoryHelper, MaterialManager } from '@awayjs/graphics';
import { TextFieldAutoSize } from '../text/TextFieldAutoSize';
import { TextFieldType } from '../text/TextFieldType';
import { TextFormat } from '../text/TextFormat';
import { TextLineMetrics } from '../text/TextLineMetrics';
import { KeyboardEvent } from '../events/KeyboardEvent';
import { TextfieldEvent } from '../events/TextfieldEvent';
import { DisplayObjectContainer } from './DisplayObjectContainer';
import { Sprite } from './Sprite';
import { TextSprite } from './TextSprite';
import { TextShape } from '../text/TextShape';
import { HTMLTextProcessor } from '../text/HTMLTextProcessor';
import { TextFormatAlign } from '../text/TextFormatAlign';
import { MouseEvent } from '../events/MouseEvent';
import { Settings } from '../Settings';
var WordStore = /** @class */ (function () {
    function WordStore(size) {
        if (size === void 0) { size = 40; }
        this.index = -1;
        this.store = Array.from({ length: size }, function (e) { return ({
            x: Infinity, y: Infinity, start: 0, width: 0, len: 0
        }); });
    }
    WordStore.prototype.put = function (start, x, y, width, len) {
        this.index++;
        var word = this.store[this.index] || (this.store[this.index] = {});
        word.start = start;
        word.x = x;
        word.y = y;
        word.width = width;
        word.len = len;
        return word;
    };
    Object.defineProperty(WordStore.prototype, "last", {
        get: function () {
            return this.store[this.index];
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(WordStore.prototype, "length", {
        get: function () {
            return this.index + 1;
        },
        set: function (v) {
            this.index = v - 1;
        },
        enumerable: false,
        configurable: true
    });
    WordStore.prototype.get = function (index) {
        return this.store[index];
    };
    WordStore.prototype.free = function () {
        this.index = -1;
    };
    WordStore.prototype.dispose = function () {
        this.store = null;
        this.index = 0;
    };
    return WordStore;
}());
var MNEMOS = [
    {
        test: /&apos;/g,
        replace: '\''
    },
    {
        test: /&gt;/g,
        replace: '>'
    }
];
/**
 * The TextField class is used to create display objects for text display and
 * input. <ph outputclass="flexonly">You can use the TextField class to
 * perform low-level text rendering. However, in Flex, you typically use the
 * Label, Text, TextArea, and TextInput controls to process text. <ph
 * outputclass="flashonly">You can give a text field an instance name in the
 * Property inspector and use the methods and properties of the TextField
 * class to manipulate it with ActionScript. TextField instance names are
 * displayed in the Movie Explorer and in the Insert Target Path dialog box in
 * the Actions panel.
 *
 * <p>To create a text field dynamically, use the <code>TextField()</code>
 * constructor.</p>
 *
 * <p>The methods of the TextField class let you set, select, and manipulate
 * text in a dynamic or input text field that you create during authoring or
 * at runtime. </p>
 *
 * <p>ActionScript provides several ways to format your text at runtime. The
 * TextFormat class lets you set character and paragraph formatting for
 * TextField objects. You can apply Cascading Style Sheets(CSS) styles to
 * text fields by using the <code>TextField.styleSheet</code> property and the
 * StyleSheet class. You can use CSS to style built-in HTML tags, define new
 * formatting tags, or apply styles. You can assign HTML formatted text, which
 * optionally uses CSS styles, directly to a text field. HTML text that you
 * assign to a text field can contain embedded media(movie clips, SWF files,
 * GIF files, PNG files, and JPEG files). The text wraps around the embedded
 * media in the same way that a web browser wraps text around media embedded
 * in an HTML document. </p>
 *
 * <p>Flash Player supports a subset of HTML tags that you can use to format
 * text. See the list of supported HTML tags in the description of the
 * <code>htmlText</code> property.</p>
 *
 * @event change                    Dispatched after a control value is
 *                                  modified, unlike the
 *                                  <code>textInput</code> event, which is
 *                                  dispatched before the value is modified.
 *                                  Unlike the W3C DOM Event Model version of
 *                                  the <code>change</code> event, which
 *                                  dispatches the event only after the
 *                                  control loses focus, the ActionScript 3.0
 *                                  version of the <code>change</code> event
 *                                  is dispatched any time the control
 *                                  changes. For example, if a user types text
 *                                  into a text field, a <code>change</code>
 *                                  event is dispatched after every keystroke.
 * @event link                      Dispatched when a user clicks a hyperlink
 *                                  in an HTML-enabled text field, where the
 *                                  URL begins with "event:". The remainder of
 *                                  the URL after "event:" is placed in the
 *                                  text property of the LINK event.
 *
 *                                  <p><b>Note:</b> The default behavior,
 *                                  adding the text to the text field, occurs
 *                                  only when Flash Player generates the
 *                                  event, which in this case happens when a
 *                                  user attempts to input text. You cannot
 *                                  put text into a text field by sending it
 *                                  <code>textInput</code> events.</p>
 * @event scroll                    Dispatched by a TextField object
 *                                  <i>after</i> the user scrolls.
 * @event textInput                 Flash Player dispatches the
 *                                  <code>textInput</code> event when a user
 *                                  enters one or more characters of text.
 *                                  Various text input methods can generate
 *                                  this event, including standard keyboards,
 *                                  input method editors(IMEs), voice or
 *                                  speech recognition systems, and even the
 *                                  act of pasting plain text with no
 *                                  formatting or style information.
 * @event textInteractionModeChange Flash Player dispatches the
 *                                  <code>textInteractionModeChange</code>
 *                                  event when a user changes the interaction
 *                                  mode of a text field. for example on
 *                                  Android, one can toggle from NORMAL mode
 *                                  to SELECTION mode using context menu
 *                                  options
 */
var TextField = /** @class */ (function (_super) {
    __extends(TextField, _super);
    /**
     * Creates a new TextField instance. After you create the TextField instance,
     * call the <code>addChild()</code> or <code>addChildAt()</code> method of
     * the parent DisplayObjectContainer object to add the TextField instance to
     * the display list.
     *
     * <p>The default size for a text field is 100 x 100 pixels.</p>
     */
    function TextField() {
        var _this = _super.call(this) || this;
        _this.textOffsetX = 0;
        _this.textOffsetY = 0;
        _this._selectionBeginIndex = 0;
        _this._selectionEndIndex = 0;
        _this._biggestLine = 0;
        /**
         * Renderable text, used for computing a glyphs
         * @private
         */
        _this._iText = '';
        /**
         * Place where is diff is begun to end
         * @private
         */
        _this._iTextDiffStart = 0;
        /**
         * Original text passed to field
         * @private
         */
        _this._text = '';
        _this._iTextWoLineBreaks = ''; // _iText without line breaks
        _this._newTextFormat = new TextFormat();
        _this.cursorIntervalID = -1;
        _this.chars_codes = [];
        _this.chars_width = [];
        _this.tf_per_char = [];
        // stores offset and length and width for each word
        _this.words = new WordStore(10);
        _this._textRuns_formats = []; // stores textFormat for each textrun
        // stores words-offset, word-count and width for each textrun
        _this._textRuns_words = [];
        _this._paragraph_textRuns_indices = []; // stores textFormat for each textrun
        _this._maxWidthLine = 0;
        _this._labelData = null;
        _this.lines_wordStartIndices = [];
        _this.lines_wordEndIndices = [];
        _this.lines_start_y = [];
        _this.lines_start_x = [];
        _this.lines_charIdx_start = [];
        _this.lines_charIdx_end = [];
        _this.lines_width = [];
        _this.lines_height = [];
        _this.lines_numSpacesPerline = [];
        _this.char_positions_x = [];
        _this.char_positions_y = [];
        // keeping track of the original textfield that was used for cloning this one.
        _this.sourceTextField = null;
        _this._maskWidth = 0;
        _this._maskHeight = 0;
        _this._maskTextOffsetX = 0;
        _this._maskTextOffsetY = 0;
        _this.isStatic = false;
        _this._internalScale = new Vector3D(1, 1, 1);
        _this.onKeyDelegate = function (event) { return _this.onKey(event); };
        _this.startSelectionByMouseDelegate = function (event) { return _this.startSelectionByMouse(event); };
        _this.stopSelectionByMouseDelegate = function (event) { return _this.stopSelectionByMouse(event); };
        _this.updateSelectionByMouseDelegate = function (event) { return _this.updateSelectionByMouse(event); };
        _this._onClipboardPasteDelegate = function (event) { return _this.onClipboardPaste(event); };
        _this.cursorIntervalID = -1;
        _this._tabEnabled = true;
        _this.cursorType = '';
        _this.textOffsetX = 0;
        _this.textOffsetY = 0;
        _this.textShapes = {};
        _this._textColor = 0;
        _this._width = 100;
        _this._height = 100;
        _this._textWidth = 0;
        _this._textHeight = 0;
        _this._type = TextFieldType.DYNAMIC;
        _this._selectable = false;
        _this._numLines = 0;
        _this.multiline = false;
        _this._autoSize = TextFieldAutoSize.NONE;
        _this._wordWrap = false;
        _this._background = false;
        _this._backgroundColor = 0xffffff;
        _this._border = false;
        _this._borderColor = 0x000000;
        _this.html = false;
        _this.maxChars = 0;
        _this._selectionBeginIndex = 0;
        _this._selectionEndIndex = 0;
        _this._scrollH = 0;
        _this._scrollV = 0;
        _this._textFormats = [];
        _this._graphics = Graphics.getGraphics(); //unique graphics object for each TextField
        _this.mouseEnabled = _this._selectable;
        return _this;
    }
    TextField.getNewTextField = function () {
        return (TextField._textFields.length) ? TextField._textFields.pop() : new TextField();
    };
    TextField.clearPool = function () {
        TextField._textFields = [];
    };
    TextField.prototype.updateMaskMode = function () {
        // mask needed
        if (this.inMaskMode) {
            if (this._maskWidth != this._width || this._maskHeight != this._height ||
                this._maskTextOffsetX != this.textOffsetX || this._maskTextOffsetY != this.textOffsetY) {
                this._maskWidth = this._width;
                this._maskHeight = this._height;
                this._maskTextOffsetX = this.textOffsetX;
                this._maskTextOffsetY = this.textOffsetY;
                this.maskChild.graphics.clear();
                this.maskChild.graphics.beginFill(0xffffff);
                this.maskChild.graphics.drawRect(this.textOffsetX, this.textOffsetY, this._width, this._height);
                this.maskChild.graphics.endFill();
            }
            this._graphics.clear();
        }
        if (!this.inMaskMode) {
            // 	masking already setup
            // 	just make sure the mask has correct size
            this.inMaskMode = true;
            if (!this.maskChild)
                this.maskChild = new Sprite();
            if (!this.textChild)
                this.textChild = new TextSprite();
            this.textChild.mouseEnabled = false;
            this.textChild.parentTextField = this;
            this.maskChild.mouseEnabled = false;
            this.maskChild.graphics.beginFill(0xffffff);
            this.maskChild.graphics.drawRect(this.textOffsetX, this.textOffsetY, this._width, this._height);
            this.maskChild.graphics.endFill();
            this.addChild(this.maskChild);
            this.addChild(this.textChild);
            this.maskChild.visible = false;
            this._graphics.clear();
            this.targetGraphics = this.textChild.graphics;
        }
        // only use masking if needed:
        if (this._textWidth > this._width || this._textHeight > this._height) {
            this.textChild.scriptMask = this.maskChild;
        }
        else {
            this.textChild.scriptMask = null;
        }
        return;
    };
    TextField.prototype.getMouseCursor = function () {
        return this.cursorType;
    };
    Object.defineProperty(TextField.prototype, "isInFocus", {
        get: function () {
            return this._isInFocus;
        },
        set: function (value) {
        },
        enumerable: false,
        configurable: true
    });
    TextField.prototype.setFocus = function (value, fromMouseDown, sendSoftKeyEvent) {
        if (fromMouseDown === void 0) { fromMouseDown = false; }
        if (sendSoftKeyEvent === void 0) { sendSoftKeyEvent = true; }
        if (this._isInFocus == value) {
            return;
        }
        _super.prototype.setFocus.call(this, value, fromMouseDown, sendSoftKeyEvent);
        this.enableInput(value);
        if (!this._selectable) {
            return;
        }
        // if (value) {
        // 	this.setSelection(0, this._iText.length);
        // 	// check if a adapter exists
        // 	if (sendSoftKeyEvent && this.adapter != this) {
        // 		// todo: create a ITextFieldAdapter, so we can use selectText() without casting to any
        // 		(<any> this.adapter).selectTextField(fromMouseDown);
        // 	}
        // } else {
        // 	this.setSelection(0, 0);
        // }
        this.setSelection(0, 0);
        this._glyphsDirty = true;
        this.invalidate();
    };
    TextField.prototype.enableInput = function (enable) {
        if (enable === void 0) { enable = true; }
        if (this.cursorIntervalID >= 0) {
            window.clearInterval(this.cursorIntervalID);
            this.cursorIntervalID = -1;
        }
        if (enable && this._isInFocus && this.selectable) {
            this.drawSelectionGraphics();
            var myThis_1 = this;
            this.cursorIntervalID = window.setInterval(function () {
                myThis_1.cursorBlinking = !myThis_1.cursorBlinking;
                if (!myThis_1.selectable) {
                    myThis_1.cursorBlinking = true;
                }
                myThis_1._shapesDirty = true;
                myThis_1.invalidate();
            }, 500);
        }
        // FFUUU, this not working because we prevent events, and Ctrl + V/C not bubbled to document
        // will use mannual handling a Ctrl + V/C
        /*
        if (enable) {
            document.addEventListener('paste', this._onClipboardPasteDelegate);
        } else {
            document.removeEventListener('paste', this._onClipboardPasteDelegate);
        }*/
    };
    TextField.prototype.findCharIdxForMouse = function (event) {
        var myPoint = new Point(event.position.x, event.position.y);
        var lineIdx = this.getLineIndexAtPoint(myPoint.x, myPoint.y);
        var charIdx = this.getCharIndexAtPoint(myPoint.x, myPoint.y, lineIdx);
        if (lineIdx >= this.lines_start_x.length) {
            lineIdx = this.lines_start_x.length - 1;
        }
        if (lineIdx < 0) {
            lineIdx = 0;
        }
        if (lineIdx >= 0 && charIdx < 0 && this.lines_start_x[lineIdx] !== undefined) {
            if (myPoint.x <= this.lines_start_x[lineIdx]) {
                charIdx = this.lines_charIdx_start[lineIdx];
            }
            else {
                charIdx = this.lines_charIdx_end[lineIdx];
            }
        }
        if (lineIdx < 0 || charIdx < 0) {
            charIdx = 0;
        }
        return charIdx;
    };
    TextField.prototype.startSelectionByMouse = function (event) {
        this._selectionBeginIndex = this.findCharIdxForMouse(event);
        this._selectionEndIndex = this._selectionBeginIndex;
        if (this.cursorShape)
            this.cursorShape.invalidate();
        this.cursorShape = undefined;
        if (this.bgShapeSelect)
            this.bgShapeSelect.invalidate();
        this.bgShapeSelect = undefined;
        this._glyphsDirty = true;
        this._shapesDirty = true;
        this._textShapesDirty = true;
        this.cursorBlinking = false;
        this.drawSelectionGraphics();
    };
    TextField.prototype.stopSelectionByMouse = function (event) {
        this._selectionEndIndex = this.findCharIdxForMouse(event);
        //console.log("stopSelectionByMouse", this._selectionBeginIndex, this._selectionEndIndex);
        this._glyphsDirty = true;
        this.reConstruct();
        this.drawSelectionGraphics();
    };
    TextField.prototype.updateSelectionByMouse = function (event) {
        this._selectionEndIndex = this.findCharIdxForMouse(event);
        if (this.bgShapeSelect)
            this.bgShapeSelect.invalidate();
        this.bgShapeSelect = undefined;
        //console.log("updateSelectionByMouse", this._selectionBeginIndex, this._selectionEndIndex);
        this._glyphsDirty = true;
        this.reConstruct();
        this.drawSelectionGraphics();
    };
    TextField.prototype.drawSelectionGraphics = function () {
        if (this._selectionBeginIndex < 0) {
            this._selectionBeginIndex = 0;
        }
        if (this._selectionBeginIndex > this.char_positions_x.length) {
            this._selectionBeginIndex = this.char_positions_x.length;
        }
        if (this._selectionEndIndex < 0) {
            this._selectionEndIndex = 0;
        }
        if (this._selectionEndIndex > this.char_positions_x.length) {
            this._selectionEndIndex = this.char_positions_x.length;
        }
        if (this._selectionBeginIndex === this._selectionEndIndex) {
            this.showSelection = false;
            this.drawCursor();
        }
        else {
            this.showSelection = true;
            this.cursorBlinking = true; // disable cursor if text select mode
            this.drawSelectedBG();
        }
    };
    TextField.prototype.scrollToCursor = function (x, y) {
        // if(!this.textChild){
        //     return;
        // }
        // if(x>this._width){
        //     this.textChild.x-=10;
        // }
        // if(x<Math.abs(this.textChild.x)){
        //     this.textChild.x=this.textChild.x+x+2;
        // }
        // if(this.textChild.x<(this._width-this.textChild.width)){
        //     this.textChild.x=this._width-this.textChild.width;
        // }
        // if(this.textChild.x>0){
        //     this.textChild.x=0;
        // }
    };
    TextField.prototype.drawCursor = function () {
        this._shapesDirty = true;
        if (this.cursorBlinking || !this.selectable || this.selectionBeginIndex !== this.selectionEndIndex) {
            return;
        }
        var x = 0;
        var y = 0;
        var tf = this._newTextFormat;
        if (this.char_positions_x.length == 0) {
            x = this.textOffsetX + (this._width / 2) + this._textWidth / 2;
            if (tf.align == 'justify') {
                // do nothing
            }
            else if (tf.align == 'center') {
                // do nothing
            }
            else if (tf.align == 'right') {
                x = this.textOffsetX + this._width - 2;
            }
            else if (tf.align == 'left') {
                x = this.textOffsetX + 4 + this._textWidth;
            }
        }
        else if (this._selectionBeginIndex == this.char_positions_x.length) {
            x = this.char_positions_x[this._selectionBeginIndex - 1] + this.chars_width[this._selectionBeginIndex - 1];
            y = this.char_positions_y[this._selectionBeginIndex - 1];
            tf = this.tf_per_char[this._selectionBeginIndex - 1];
        }
        else {
            x = this.char_positions_x[this._selectionBeginIndex];
            y = this.char_positions_y[this._selectionBeginIndex];
            tf = this.tf_per_char[this._selectionBeginIndex];
        }
        tf.font_table.initFontSize(tf.size);
        var height = tf.font_table.getLineHeight();
        var color = this.getTextColorForTextFormat(tf);
        var cursorScale = this.internalScale.x;
        if (cursorScale <= 0) {
            cursorScale = 1;
        }
        var cursorRect = [x - (0.5 * cursorScale), y, cursorScale, height];
        if (!this.cursorShape) {
            this.cursorShape = GraphicsFactoryHelper.drawRectangles(cursorRect, color, 1);
            this.cursorShape.usages++; //TODO: get rid of this memory lea
        }
        else {
            GraphicsFactoryHelper.updateRectanglesShape(this.cursorShape, cursorRect);
        }
        if (this.cursorShape.style.color !== color) {
            var alpha = ColorUtils.float32ColorToARGB(color)[0];
            var obj = MaterialManager.getMaterialForColor(color, (alpha / 255) || 1);
            if (obj.colorPos) {
                this.cursorShape.style = new Style();
                var sampler = new ImageSampler();
                obj.material.animateUVs = true;
                this.cursorShape.style.color = color;
                this.cursorShape.style.addSamplerAt(sampler, obj.material.getTextureAt(0));
                this.cursorShape.style.uvMatrix = new Matrix(0, 0, 0, 0, obj.colorPos.x, obj.colorPos.y);
            }
        }
        this.scrollToCursor(x, y);
    };
    TextField.prototype.drawSelectedBG = function () {
        this._shapesDirty = true;
        this._textShapesDirty = true;
        if (this._selectionBeginIndex < 0) {
            this._selectionBeginIndex = 0;
        }
        if (this._selectionBeginIndex > this.char_positions_x.length) {
            this._selectionBeginIndex = this.char_positions_x.length;
        }
        var select_start = this._selectionBeginIndex;
        var select_end = this._selectionEndIndex;
        if (this._selectionEndIndex < this._selectionBeginIndex) {
            select_start = this._selectionEndIndex;
            select_end = this._selectionBeginIndex;
        }
        var x = 0;
        var y = 0;
        var oldy = -1;
        var tf = null;
        var startx = -1;
        var width = 0;
        var height = 0;
        var rectangles = [];
        if (this.char_positions_x.length != 0 && this._selectionEndIndex != this._selectionBeginIndex) {
            var len = (select_end > this.char_positions_x.length) ? this.char_positions_x.length : select_end;
            //console.log(select_start, select_end);
            for (var i = select_start; i < len; i++) {
                if (i == this.char_positions_x.length) {
                    x = this.char_positions_x[i - 1] + this.chars_width[i - 1];
                    y = this.char_positions_y[i - 1];
                    tf = this.tf_per_char[i - 1];
                }
                else {
                    x = this.char_positions_x[i];
                    y = this.char_positions_y[i];
                    tf = this.tf_per_char[i];
                }
                if (startx < 0) {
                    startx = x;
                }
                if (oldy >= 0 && oldy != y) {
                    // new line
                    rectangles.push(startx, oldy, width, height);
                    width = 0;
                    startx = x;
                }
                width += this.chars_width[i];
                oldy = y;
                tf.font_table.initFontSize(tf.size);
                height = Math.max(height, tf.font_table.getLineHeight());
            }
        }
        // if (this.bgShapeSelect) {
        // 	this.bgShapeSelect.dispose();
        // 	this.bgShapeSelect=null;
        // }
        if (width > 0) {
            rectangles.push(startx, oldy, width, height);
            if (!this.bgShapeSelect) {
                this.bgShapeSelect = GraphicsFactoryHelper.drawRectangles(rectangles, 0x0, 1);
                this.bgShapeSelect.usages++; //TODO: get rid of this memory leak
            }
            else {
                GraphicsFactoryHelper.updateRectanglesShape(this.bgShapeSelect, rectangles);
            }
            return;
        }
        this.scrollToCursor(startx + width, oldy + height);
    };
    TextField.prototype.drawBG = function () {
        //TODO this fixes masking using static textfields, but dynamic textfields still have problems. (see addChild(this.maskChild))
        if (this._background) {
            this._graphics.beginFill(this.backgroundColor, 1);
            this._graphics.drawRect(this.textOffsetX, this.textOffsetY, this.width, this.height);
            this._graphics.endFill();
        }
        //create a hitArea for the textfield
        var pickObject = this.pickObject;
        if (!pickObject) {
            this.pickObject = pickObject || (pickObject = new Sprite());
            pickObject.pickObjectFromTimeline = true;
        }
        var graphics = pickObject.graphics;
        graphics.clear();
        graphics.beginFill(0x000000, 1);
        graphics.drawRect(this.textOffsetX, this.textOffsetY, this.width, this.height);
        graphics.endFill();
    };
    TextField.prototype.drawBorder = function () {
        var half_thickness_x = this.border ? 0.25 * this.internalScale.x : 0;
        var half_thickness_y = this.border ? 0.25 * this.internalScale.y : 0;
        this._graphics.beginFill(this._borderColor, 1);
        this._graphics.drawRect(this.textOffsetX, this.textOffsetY, this._width, half_thickness_y * 2);
        this._graphics.drawRect(this.textOffsetX, this.textOffsetY + this._height - half_thickness_y * 2, this._width, half_thickness_y * 2);
        this._graphics.drawRect(this.textOffsetX, this.textOffsetY + half_thickness_y * 2, half_thickness_x * 2, this._height - half_thickness_y * 2);
        this._graphics.drawRect(this.textOffsetX + this._width - half_thickness_x * 2, this.textOffsetY + half_thickness_y * 2, half_thickness_x * 2, this._height - half_thickness_y * 2);
        this._graphics.endFill();
    };
    TextField.prototype.getTextShapeForIdentifierAndFormat = function (id, format) {
        if (this.textShapes[id]) {
            return this.textShapes[id];
        }
        return (this.textShapes[id] = new TextShape(format, id));
    };
    Object.defineProperty(TextField.prototype, "autoSize", {
        get: function () {
            return this._autoSize;
        },
        set: function (value) {
            if (this._autoSize == value)
                return;
            if (typeof value === 'string') {
                if (value != TextFieldAutoSize.CENTER &&
                    value != TextFieldAutoSize.NONE &&
                    value != TextFieldAutoSize.LEFT &&
                    value != TextFieldAutoSize.RIGHT) {
                    return;
                }
            }
            else {
                if (typeof value === 'boolean') {
                    if (value)
                        value = TextFieldAutoSize.LEFT;
                    else
                        value = TextFieldAutoSize.NONE;
                }
                if (typeof value === 'number') {
                    if (value > 0)
                        value = TextFieldAutoSize.LEFT;
                    else
                        value = TextFieldAutoSize.NONE;
                }
            }
            this._autoSize = value;
            this._positionsDirty = true;
            if (this._autoSize != TextFieldAutoSize.NONE)
                this.invalidate();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "internalScale", {
        get: function () {
            return this._internalScale;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "assetType", {
        /**
         *
         * @returns {string}
         */
        get: function () {
            return TextField.assetType;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "background", {
        get: function () {
            return this._background;
        },
        set: function (value) {
            if (this._background == value)
                return;
            this._background = value;
            this._shapesDirty = true;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "backgroundColor", {
        get: function () {
            return this._backgroundColor;
        },
        set: function (value) {
            this._backgroundColor = value;
            this._shapesDirty = true;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "border", {
        get: function () {
            return this._border;
        },
        set: function (value) {
            if (value == this._border)
                return;
            this._border = value;
            this._shapesDirty = true;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "borderColor", {
        get: function () {
            return this._borderColor;
        },
        set: function (value) {
            if (value == this.borderColor)
                return;
            this._borderColor = value;
            this._shapesDirty = true;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "bottomScrollV", {
        /**
         * An integer(1-based index) that indicates the bottommost line that is
         * currently visible in the specified text field. Think of the text field as
         * a window onto a block of text. The <code>scrollV</code> property is the
         * 1-based index of the topmost visible line in the window.
         *
         * <p>All the text between the lines indicated by <code>scrollV</code> and
         * <code>bottomScrollV</code> is currently visible in the text field.</p>
         */
        get: function () {
            return this._bottomScrollV;
        },
        set: function (value) {
            if (value == this._bottomScrollV)
                return;
            this._bottomScrollV = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "caretIndex", {
        /**
         * The index of the insertion point(caret) position. If no insertion point
         * is displayed, the value is the position the insertion point would be if
         * you restored focus to the field(typically where the insertion point last
         * was, or 0 if the field has not had focus).
         *
         * <p>Selection span indexes are zero-based(for example, the first position
         * is 0, the second position is 1, and so on).</p>
         */
        get: function () {
            return this._caretIndex;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "height", {
        /**
         *
         */
        get: function () {
            if (this._autoSize != TextFieldAutoSize.NONE)
                this.reConstruct();
            return this._height;
        },
        set: function (val) {
            if (this._height == val)
                return;
            if (this._autoSize != TextFieldAutoSize.NONE)
                return;
            this._height = val;
            this._positionsDirty = true;
            this.invalidate();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "htmlText", {
        get: function () {
            return this._htmlText;
        },
        set: function (value) {
            value = (value == undefined) ? '' : value.toString();
            if (value == this._htmlText)
                return;
            this._htmlText = value;
            var processedText = HTMLTextProcessor.get().processHTML(this, value);
            // 	text might be the same,
            //	we still need to set textDirty, because formatting might have changed
            //console.log("html out",  textProps.text);
            this._labelData = null;
            this._text = processedText;
            this._iText = processedText;
            this._iTextWoLineBreaks = processedText.replace(/(\r\n|\n|\\n|\r)/gm, '');
            this._textDirty = true;
            //console.log("set text", value, "on" , this);
            this.invalidate();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "length", {
        /**
         * The number of characters in a text field. A character such as tab
         * (<code>\t</code>) counts as one character.
         */
        get: function () {
            return this._iText.length;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "maxScrollH", {
        /**
         * The maximum value of <code>scrollH</code>.
         */
        get: function () {
            this.reConstruct();
            return this._maxScrollH;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "maxScrollV", {
        /**
         * The maximum value of <code>scrollV</code>.
         */
        get: function () {
            this.reConstruct();
            return this._maxScrollV;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "numLines", {
        /**
         * Defines the number of text lines in a multiline text field. If
         * <code>wordWrap</code> property is set to <code>true</code>, the number of
         * lines increases when text wraps.
         */
        get: function () {
            this.reConstruct();
            return this._numLines;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "restrict", {
        get: function () {
            return this._restrict;
        },
        set: function (value) {
            if (value == this._restrict)
                return;
            this._restrict = value;
            this._restrictRegex = null;
            if (typeof value == 'undefined')
                return;
            value = value.toString();
            // flash allows something like -9 to be used instaed 0-9. fix this here:
            if (value.length >= 2 && value[0] == '-' && !isNaN(parseInt(value[1])))
                value = '0' + value;
            // remove all backslashes. flash does not allow to use backslash as allowed char
            value = value.replace(/\\/g, '');
            // remove all ^. flash does not allow to use ^ as allowed char
            value = value.replace(/\^/g, '');
            // make sure all "-" are escaped if they are not used to define a range
            // eslint-disable-next-line no-useless-escape
            value = value.replace(/([^a-zA-Z0-9])\-/g, '$1\\-');
            // escape all special chars so that regex will be valid
            //todo: should be able to do the following with a single regex:
            value = value.replace(/\./g, '\\.');
            // eslint-disable-next-line no-useless-escape
            value = value.replace(/\</g, '\\<');
            // eslint-disable-next-line no-useless-escape
            value = value.replace(/\>/g, '\\>');
            value = value.replace(/\+/g, '\\+');
            value = value.replace(/\*/g, '\\*');
            value = value.replace(/\?/g, '\\?');
            value = value.replace(/\[/g, '\\[');
            value = value.replace(/\]/g, '\\]');
            value = value.replace(/\$/g, '\\$');
            value = value.replace(/\(/g, '\\(');
            value = value.replace(/\)/g, '\\)');
            value = value.replace(/\{/g, '\\{');
            value = value.replace(/\}/g, '\\}');
            // eslint-disable-next-line no-useless-escape
            value = value.replace(/\=/g, '\\=');
            // eslint-disable-next-line no-useless-escape
            value = value.replace(/\!/g, '\\!');
            // eslint-disable-next-line no-useless-escape
            value = value.replace(/\:/g, '\\:');
            value = value.replace(/\|/g, '\\|');
            value = value.replace(/\//g, '\\/');
            // eslint-disable-next-line no-useless-escape
            value = value.replace(/\%/g, '\\%');
            this._restrictRegex = new RegExp('[^' + value + ']', 'g');
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "scrollH", {
        get: function () {
            return this._scrollH;
        },
        set: function (value) {
            if (value == this._scrollH)
                return;
            this._scrollH = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "scrollV", {
        get: function () {
            return this._scrollV;
        },
        set: function (value) {
            var rounded = Math.round(value);
            if (rounded === this._scrollV)
                return;
            this._scrollV = rounded;
            if (this._scrollV > this._maxScrollV)
                this._scrollV = this._maxScrollV;
            if (this._scrollV <= 0) {
                this._scrollV = 0;
            }
            if (!this.textChild) {
                return;
            }
            // unsafe
            this.textChild.y = -this.lines_start_y[this._scrollV];
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "selectable", {
        get: function () {
            return this._selectable;
        },
        set: function (value) {
            if (this.selectable == value) {
                return;
            }
            this._selectable = value;
            this.mouseEnabled = value;
            this.cursorType = value ? 'text' : '';
            if (value) {
                this.addEventListener(MouseEvent.DRAG_START, this.startSelectionByMouseDelegate);
                this.addEventListener(MouseEvent.DRAG_STOP, this.stopSelectionByMouseDelegate);
                this.addEventListener(MouseEvent.DRAG_MOVE, this.updateSelectionByMouseDelegate);
            }
            else {
                this.removeEventListener(MouseEvent.DRAG_START, this.startSelectionByMouseDelegate);
                this.removeEventListener(MouseEvent.DRAG_STOP, this.stopSelectionByMouseDelegate);
                this.removeEventListener(MouseEvent.DRAG_MOVE, this.updateSelectionByMouseDelegate);
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "selectionBeginIndex", {
        /**
         * The zero-based character index value of the first character in the current
         * selection. For example, the first character is 0, the second character is
         * 1, and so on. If no text is selected, this property is the value of
         * <code>caretIndex</code>.
         */
        get: function () {
            return this._selectionBeginIndex;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "selectionEndIndex", {
        /**
         * The zero-based character index value of the last character in the current
         * selection. For example, the first character is 0, the second character is
         * 1, and so on. If no text is selected, this property is the value of
         * <code>caretIndex</code>.
         */
        get: function () {
            return this._selectionEndIndex;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "text", {
        /**
         * A string that is the current text in the text field. Lines are separated
         * by the carriage return character(<code>'\r'</code>, ASCII 13). This
         * property contains unformatted text in the text field, without HTML tags.
         *
         * <p>To get the text in HTML form, use the <code>htmlText</code>
         * property.</p>
         */
        get: function () {
            return this._text;
        },
        set: function (value) {
            value = (value == undefined) ? '' : value.toString();
            value = value.replace(String.fromCharCode(160), ' ');
            if (this._text == value && !this._newFormatDirty)
                return;
            this._newFormatDirty = false;
            this._labelData = null;
            this._text = value;
            if (value != '' && ((value.charCodeAt(value.length - 1) == 13) || (value.charCodeAt(value.length - 1) == 10))) {
                value = value.slice(0, value.length - 1);
            }
            if (value != '' && (value.length >= 3
                && value[value.length - 1] == 'n' && value[value.length - 2] == '\\' && value[value.length - 3] == '\\')) {
                value = value.slice(0, value.length - 3);
            }
            if (value != '' && (value.length >= 3 && value[value.length - 1] == 'n' && value[value.length - 2] == '\\')) {
                value = value.slice(0, value.length - 2);
            }
            for (var _i = 0, MNEMOS_1 = MNEMOS; _i < MNEMOS_1.length; _i++) {
                var m = MNEMOS_1[_i];
                value = value.replace(m.test, m.replace);
            }
            this._iText = value;
            this._iTextWoLineBreaks = value.replace(/(\r\n|\n|\\n|\r)/gm, '');
            this._textFormats = [this.newTextFormat];
            this._textFormatsIdx = [this._iText.length];
            this._textDirty = true;
            //console.log("set text", value, "on" , this);
            this.invalidate();
        },
        enumerable: false,
        configurable: true
    });
    TextField.prototype.setLabelData = function (labelData) {
        this._labelData = labelData;
        this.isStatic = true;
        this._iText = '';
        this._iTextWoLineBreaks = '';
        this._textDirty = false;
        this._positionsDirty = false;
        this._glyphsDirty = true;
        this.invalidate();
    };
    Object.defineProperty(TextField.prototype, "newTextFormat", {
        get: function () {
            return this._newTextFormat.clone();
        },
        set: function (value) {
            if (!value)
                throw new Error('TextField::: set newTextFormat - no value!');
            if (value.equal(this._newTextFormat))
                return;
            this._newTextFormat = value.clone();
            this._newFormatDirty = true;
            this._textShapesDirty = true;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "scaleX", {
        /**
         * Indicates the horizontal scale(percentage) of the object as applied from
         * the registration point. The default registration point is(0,0). 1.0
         * equals 100% scale.
         *
         * <p>Scaling the local coordinate system changes the <code>x</code> and
         * <code>y</code> property values, which are defined in whole pixels. </p>
         */
        get: function () {
            return this._transform.scale.x;
        },
        set: function (val) {
            if (this._transform.scale.x == val) {
                return;
            }
            this._setScaleX(val);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "scaleY", {
        /**
         * Indicates the vertical scale(percentage) of an object as applied from the
         * registration point of the object. The default registration point is(0,0).
         * 1.0 is 100% scale.
         *
         * <p>Scaling the local coordinate system changes the <code>x</code> and
         * <code>y</code> property values, which are defined in whole pixels. </p>
         */
        get: function () {
            return this._transform.scale.y;
        },
        set: function (val) {
            if (this._transform.scale.y == val) {
                return;
            }
            this._setScaleY(val);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "textColor", {
        get: function () {
            return this._textColor;
        },
        set: function (value) {
            if (this._textColor == value) {
                return;
            }
            this._textColor = value;
            //this._textFormat.color=value;
            if (this._textFormats) {
                var i = this._textFormats.length;
                while (i > 0) {
                    i--;
                    if (this._textFormats[i])
                        this._textFormats[i].color = value;
                }
                this._textDirty = true;
            }
            /*if (this._textFormat
                && !this._textFormat.font_table.isAsset(TesselatedFontTable)
                && !this._textFormat.material) {
    
                if (!this.transform.colorTransform)
                    this.transform.colorTransform = new ColorTransform();
    
                this.transform.colorTransform.color = value;
                this._invalidateHierarchicalProperties(HierarchicalProperties.COLOR_TRANSFORM);
            } else {
                this._glyphsDirty = true;
    
                if (this._implicitPartition)
                    this._implicitPartition.invalidateEntity(this);
            //}*/
            this._glyphsDirty = true;
            this.invalidate();
        },
        enumerable: false,
        configurable: true
    });
    TextField.prototype.getTextColorForTextFormat = function (format) {
        if (format.hasPropertySet('color')) {
            return format.color;
        }
        return this._textColor;
    };
    Object.defineProperty(TextField.prototype, "textInteractionMode", {
        /**
         * The interaction mode property, Default value is
         * TextInteractionMode.NORMAL. On mobile platforms, the normal mode implies
         * that the text can be scrolled but not selected. One can switch to the
         * selectable mode through the in-built context menu on the text field. On
         * Desktop, the normal mode implies that the text is in scrollable as well as
         * selection mode.
         */
        get: function () {
            return this._textInteractionMode;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "textWidth", {
        /**
         * The width of the text in pixels.
         */
        get: function () {
            this.reConstruct();
            return this._textWidth;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "textHeight", {
        /**
         * The width of the text in pixels.
         */
        get: function () {
            this.reConstruct();
            if (this.type == TextFieldType.INPUT && this._iText == '') {
                this._newTextFormat.font_table.initFontSize(this._newTextFormat.size);
                return this._newTextFormat.font_table.getLineHeight();
            }
            return this._textHeight;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "type", {
        get: function () {
            return this._type;
        },
        set: function (value) {
            if (this._type == value)
                return;
            this._type = value;
            this._textDirty = true;
            if (value == TextFieldType.INPUT) {
                //this._selectable=true;
                this.enableInput(true);
                this.addEventListener(KeyboardEvent.KEYDOWN, this.onKeyDelegate);
            }
            else {
                this.enableInput(false);
                this.removeEventListener(KeyboardEvent.KEYDOWN, this.onKeyDelegate);
            }
            this.invalidate();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "x", {
        get: function () {
            if (this._autoSize != TextFieldAutoSize.NONE && !this._wordWrap) {
                this.reConstruct();
            }
            return this._transform.position.x;
        },
        set: function (val) {
            if (this._autoSize != TextFieldAutoSize.NONE && !this._wordWrap)
                this.reConstruct();
            if (this._transform.position.x == val)
                return;
            this._transform.matrix3D._rawData[12] = val;
            this._transform.invalidatePosition();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "width", {
        /**
         *
         */
        get: function () {
            if (this._autoSize != TextFieldAutoSize.NONE && !this._wordWrap) {
                this.reConstruct();
            }
            return this._width;
        },
        set: function (val) {
            if (this._width == val)
                return;
            //if (this._autoSize != TextFieldAutoSize.NONE && !this._wordWrap)
            //	return;
            this._width = val;
            this._positionsDirty = true;
            this.invalidate();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TextField.prototype, "wordWrap", {
        /**
         * The width of the text in pixels.
         */
        get: function () {
            return this._wordWrap;
        },
        set: function (val) {
            if (this._wordWrap == val)
                return;
            this._wordWrap = val;
            this._positionsDirty = true;
            if (!val)
                this.invalidate();
        },
        enumerable: false,
        configurable: true
    });
    TextField.prototype.advanceFrame = function () {
        //override for textfield
    };
    TextField.prototype.getEntity = function () {
        this.reConstruct(true);
        return this._graphics;
    };
    TextField.prototype.clear = function () {
        _super.prototype.clear.call(this);
    };
    /**
     * @inheritDoc
     */
    TextField.prototype.dispose = function () {
        this.disposeValues();
        TextField._textFields.push(this);
    };
    /**
     * @inheritDoc
     */
    TextField.prototype.disposeValues = function () {
        _super.prototype.disposeValues.call(this);
        if (this.maskChild) {
            this.maskChild.dispose();
            this.maskChild = null;
        }
        if (this.textChild) {
            this.textChild.dispose();
            this.textChild = null;
        }
        if (this.cursorShape) {
            this.cursorShape.dispose();
            this.cursorShape = null;
        }
        if (this.bgShapeSelect) {
            this.bgShapeSelect.dispose();
            this.bgShapeSelect = null;
        }
        if (this._labelData) {
            this._labelData = null;
        }
        this._resetParagraph();
        this._clearTextShapes();
    };
    /**
     * Reconstructs the Graphics for this Text-field.
     */
    TextField.prototype.reConstruct = function (buildGraphics) {
        if (buildGraphics === void 0) { buildGraphics = false; }
        if (!this._textDirty && !this._positionsDirty && !this._glyphsDirty && !this._shapesDirty)
            return;
        // Step1: init text-data
        // this step splits the text into textRuns and sort them into paragraphs
        // each textRun spans a range of words that share the same text-format
        // a textRun can not be shared between paragraphs
        // for each word, 5 numbers are stored:
        // 		char-index,
        // 		x-pos,
        // 		y-pos,
        // 		word-width,
        // 		char-count,
        // a whitespace is considered as a word
        if (this._textDirty) {
            this._positionsDirty = true;
            this._lastWordsCount = this.words.length;
            this.char_positions_x.length = 0;
            this.char_positions_y.length = 0;
            this.words.length = 0;
            this.lines_wordStartIndices.length = 0;
            this.lines_wordEndIndices.length = 0;
            this.lines_start_y.length = 0;
            this.lines_start_x.length = 0;
            this.lines_charIdx_start.length = 0;
            this.lines_charIdx_end.length = 0;
            this.lines_width.length = 0;
            this.lines_height.length = 0;
            this.lines_numSpacesPerline.length = 0;
            this._maxScrollH = 0;
            this._maxScrollV = 0;
            this._maxWidthLine = 0;
            this.buildParagraphs();
            //console.log("TextField buildParagraph", this.id, this._iText);
            //console.log("TextField buildParagraph", this.id, this._autoSize);
            //console.log("TextField buildParagraph", this.id, this._wordWrap);
            //console.log("TextField buildParagraph", this.id, this.multiline);
        }
        // 	Step 2: positioning the words
        // 	if position is dirty, the text formatting has changed.
        // 	this step will modify the word-data stored in previous step.
        //	for each word, it adjusts the x-pos and y-pos position.
        //	this step also takes care of adjusting the textWidth and textHeight,
        //	if we have AUTOSIZE!=None
        if (this._positionsDirty) {
            this._glyphsDirty = true;
            if (this._iText != '' && this._textFormats[0] != null) {
                //console.log("TextField getWordPositions", this.id, this.words);
                this.getWordPositions(this._textFormats[0]);
            }
            else {
                // this is empty text, we need to reset the text-size
                this._textWidth = 0;
                this._textHeight = 0;
                if (this._autoSize != TextFieldAutoSize.NONE) {
                    if (!this.wordWrap)
                        this.adjustPositionForAutoSize(0); //(this._width - 4)/2);
                    this._height = 4;
                    if (this._type == TextFieldType.INPUT) {
                        this._newTextFormat.font_table.initFontSize(this._newTextFormat.size);
                        this._height = this._newTextFormat.font_table.getLineHeight() + 4;
                    }
                }
                if (this._type == TextFieldType.INPUT)
                    this.drawSelectionGraphics();
            }
            this.updateMaskMode();
        }
        this._textDirty = false;
        this._positionsDirty = false;
        if (!buildGraphics)
            return;
        // 	Step 3: building the glyphs
        // 	this step is only done if this function was called when renderer collects the graphics.
        //	only than should the reconstruct function be called with "buildGraphics=true".
        // 	in this step, the text-shapes are cleared,
        //	the data for new text-shapes is collected from the font-tables
        //	and the new text-shapes are created and assigned to the graphics
        if (this._glyphsDirty) {
            //console.log("TextField buildGlyphs", this.id, this.words);
            if (this._labelData) {
                this.buildGlyphsForLabelData();
            }
            else {
                this.buildGlyphs();
            }
        }
        this._glyphsDirty = false;
        if (this._labelData) {
            return;
        }
        this.buildShapes();
        this._shapesDirty = false;
    };
    TextField.prototype.reset = function () {
        _super.prototype.reset.call(this);
        //if(this.name && typeof this.name !== "number"){
        // if the textfield has a valid name, it might have been changed by scripts.
        // in that case we want to reset it to its original state
        if (this.sourceTextField) {
            this.sourceTextField.copyTo(this);
        }
        //}
        /*if(this.adapter != this){
            (<any>this.adapter).syncTextFieldValue();
        }*/
    };
    TextField.prototype.onClipboardPaste = function (event) {
        var paste = (event.clipboardData || self.clipboardData).getData('text');
        if (!paste) {
            return;
        }
        event.preventDefault();
        if (this._selectionBeginIndex > 0 || this._selectionEndIndex > this.length) {
            this._insertNewText(paste);
            return;
        }
        this.text = paste;
    };
    TextField.prototype._resetParagraph = function () {
        this._textShapesDirty = this._textShapesDirty || this.chars_codes.length > 0;
        this.tf_per_char.length = 0;
        this.chars_width.length = 0;
        this.chars_codes.length = 0;
        this._paragraph_textRuns_indices.length = 0;
        this._textRuns_formats.length = 0;
        this._textRuns_words.length = 0;
    };
    TextField.prototype.buildParagraphs = function () {
        if (!this._textDirty) {
            return;
        }
        // clear paragraph data when clear
        if (this._iText === '' || !this._textFormats[0]) {
            this._resetParagraph();
            return;
        }
        var paragraphIndex = 0;
        var formatIndex = 0;
        var wordIndex = 0;
        var charCodeIndex = 0;
        // track char mutations between new and older.
        var codeChanges = 0;
        var linewidth = 0;
        var c_start = 0;
        var thisText = this._iText;
        var formatsCount = this._textFormatsIdx.length;
        var paragraphIndices = this._paragraph_textRuns_indices;
        var textRunFormats = this._textRuns_formats;
        var textRunWords = this._textRuns_words;
        var charCodes = this.chars_codes;
        var charTextFormats = this.tf_per_char;
        var charWidths = this.chars_width;
        var pushParagraph = function (formatIndex) {
            paragraphIndices[paragraphIndex] = formatIndex;
            paragraphIndex++;
        };
        var pushFormat = function (format, updateParagraph) {
            if (updateParagraph === void 0) { updateParagraph = false; }
            updateParagraph && pushParagraph(formatIndex);
            textRunFormats[formatIndex++] = format;
            return format;
        };
        var pushRunEntry = function (word) {
            textRunWords[wordIndex++] = word;
            return word;
        };
        var pushCharData = function (code, tf, width) {
            codeChanges += +(code !== charCodes[charCodeIndex]);
            codeChanges += +(tf !== charTextFormats[charCodeIndex]);
            codeChanges += +(width !== charWidths[charCodeIndex]);
            charCodes[charCodeIndex] = code;
            charTextFormats[charCodeIndex] = tf;
            charWidths[charCodeIndex] = width;
            charCodeIndex++;
        };
        pushParagraph(formatIndex);
        // loop over all textFormats
        for (var f = 0; f < formatsCount; f++) {
            var word_cnt = 0;
            var whitespace_cnt = 0;
            var startNewWord = true;
            var tf = this._textFormats[f];
            var maxLineWidth = this._width - (tf.indent + tf.leftMargin + tf.rightMargin);
            tf.font_table.initFontSize(tf.size);
            // if that is last format then it goes till the end of text:
            var c_end = (f === formatsCount - 1) ? thisText.length : this._textFormatsIdx[f];
            if (c_end > c_start) {
                // create a new textrun
                pushFormat(tf);
                var run = pushRunEntry({
                    start: this.words.length,
                    count: 0,
                    width: 0,
                    space: 0
                });
                // loop over all chars for this format
                //console.log("textrun tf = ", tf);
                for (var c = c_start; c < c_end; c++) {
                    var char_code = thisText.charCodeAt(c);
                    var next_char_code = thisText.charCodeAt(c + 1);
                    // skip CR, because there are only 2 variation
                    // CRLF or LF
                    if (char_code === 13 /* CHAR_CODES.CR */) {
                        char_code = 10 /* CHAR_CODES.LF */;
                    }
                    // again skip CR
                    if (char_code === 92 /* CHAR_CODES.BS */ && next_char_code === 114 /* CHAR_CODES.R */) {
                        c += 1;
                        continue;
                    }
                    // \n to LF
                    if (char_code === 92 /* CHAR_CODES.BS */ && next_char_code === 110 /* CHAR_CODES.N */) {
                        c += 1;
                        char_code = 10 /* CHAR_CODES.LF */;
                        next_char_code = thisText.charCodeAt(c + 1);
                    }
                    var isLineBreak = char_code === 10 /* CHAR_CODES.LF */;
                    if (isLineBreak) {
                        run.count = word_cnt;
                        run.width = linewidth;
                        run.space = whitespace_cnt;
                        // create a new textrun
                        pushFormat(tf, true);
                        run = pushRunEntry({
                            start: this.words.length,
                            count: 0,
                            width: 0,
                            space: 0
                        });
                        startNewWord = true;
                        whitespace_cnt = 0;
                        word_cnt = 0;
                        if (this._maxWidthLine < linewidth) {
                            this._maxWidthLine = linewidth;
                        }
                        linewidth = 0;
                        continue;
                    }
                    var char_width = tf.font_table.getCharWidth(char_code.toString());
                    var isSpace = char_code == 9 /* CHAR_CODES.TAB */ || char_code == 32 /* CHAR_CODES.SPACE */;
                    // if this is a letter, and next char is no whitespace, we add the letterSpacing to the letter-width
                    // todo: we might need to add the letterspacing also if next char is a linebreak ?
                    if (!isSpace && c < c_end - 1) {
                        if (next_char_code != 9 /* CHAR_CODES.TAB */ && next_char_code != 32 /* CHAR_CODES.SPACE */) {
                            char_width += tf.letterSpacing;
                        }
                    }
                    linewidth += char_width;
                    pushCharData(char_code, tf, char_width);
                    // we create a new word if the char is either:
                    // 	- first char of paragraph
                    //	- is a whitespace
                    //  - follows a whitespace
                    if (isSpace) {
                        //console.log("add WhiteSpace");
                        whitespace_cnt++;
                        this.words.put(charCodeIndex - 1, 0, 0, char_width, 1);
                        word_cnt++;
                        // we also make sure to begin a new word for next char (could be whitespace again)
                        startNewWord = true;
                    }
                    else {
                        // no whitespace
                        if (word_cnt > 0 && this._autoSize == TextFieldAutoSize.NONE && this._wordWrap) {
                            if (this.words.last.width + char_width >= maxLineWidth) {
                                startNewWord = true;
                            }
                        }
                        if (startNewWord) {
                            //console.log("startNewWord");
                            // create new word (either this is the first char, or the last char was whitespace)
                            this.words.put(charCodeIndex - 1, 0, 0, char_width, 1);
                            word_cnt++;
                        }
                        else {
                            // update-char length and width of active word.
                            this.words.last.width += char_width;
                            this.words.last.len += 1;
                        }
                        startNewWord = false;
                    }
                }
                run.count = word_cnt;
                run.width = linewidth;
                run.space = whitespace_cnt;
                if (this._maxWidthLine < linewidth) {
                    this._maxWidthLine = linewidth;
                }
            }
            c_start = c_end;
        }
        this._textShapesDirty = (this._textShapesDirty ||
            codeChanges > 0 ||
            charCodes.length !== charCodeIndex);
        // reduce paragraph count if will be greater that older
        paragraphIndices.length = paragraphIndex;
        textRunWords.length = wordIndex;
        textRunFormats.length = formatIndex;
        charCodes.length = charCodeIndex;
        charTextFormats.length = charCodeIndex;
        charWidths.length = charCodeIndex;
    };
    TextField.prototype.adjustPositionForAutoSize = function (newWidth) {
        var oldSize = this._width;
        this._width = 4 + newWidth;
        if (this._autoSize == TextFieldAutoSize.RIGHT) {
            this._transform.matrix3D._rawData[12] -= this._width - oldSize;
            this._transform.invalidatePosition();
        }
        else if (this._autoSize == TextFieldAutoSize.CENTER) {
            this._transform.matrix3D._rawData[12] -= (this._width - oldSize) / 2;
            this._transform.invalidatePosition();
        }
    };
    TextField.prototype.getWordPositions = function (tf) {
        /*console.log("this._iText", this._iText);
        console.log("this._width", this._width);
        console.log("this._height", this._height);*/
        /*for(var i=0; i<this.chars_codes.length; i++){
            console.log("test: ", this.chars_codes[i], this.chars_width[i]);
        }*/
        var tr = 0;
        var tr_len = this._textRuns_formats.length;
        var w = 0;
        var w_len = 0;
        var tr_length = 0;
        var format;
        var text_width = 0;
        var indent = 0;
        this._numLines = 0;
        var linecnt = 0;
        var linelength = 0;
        var word_width = 0;
        var offsety = this.textOffsetY + 2;
        //console.log("text old_width", this._width);
        //console.log("text old_x", this._transform.matrix3D._rawData[12]);
        //console.log("this._autoSize", this._autoSize);
        //console.log("this._wordWrap", this._wordWrap);
        // if we have autosize enabled, and no wordWrap, we can adjust the textfield width
        if (this._autoSize != TextFieldAutoSize.NONE && !this._wordWrap && this._textDirty) {
            var maxSizeComplete = this._maxWidthLine + tf.indent
                + tf.leftMargin + tf.rightMargin;
            this.adjustPositionForAutoSize(maxSizeComplete);
        }
        var maxLineWidth = this._width - (tf.indent + tf.leftMargin + tf.rightMargin);
        var p = 0;
        var p_len = this._paragraph_textRuns_indices.length;
        linecnt = 0;
        this.lines_wordStartIndices.length = 0;
        this.lines_wordEndIndices.length = 0;
        this.lines_start_y.length = 0;
        this.lines_start_x.length = 0;
        this.lines_width.length = 0;
        this.lines_height.length = 0;
        this.lines_numSpacesPerline.length = 0;
        this.lines_charIdx_start.length = 0;
        this.lines_charIdx_end.length = 0;
        var lines_heights = [];
        var lines_formats = [];
        // loop over all paragraphs
        for (p = 0; p < p_len; p++) {
            tr_len = (p == (p_len - 1)) ? this._textRuns_formats.length : this._paragraph_textRuns_indices[p + 1];
            tr_length = 0;
            lines_heights[lines_heights.length] = 0;
            for (tr = this._paragraph_textRuns_indices[p]; tr < tr_len; tr++) {
                format = this._textRuns_formats[tr];
                format.font_table.initFontSize(format.size);
                if (lines_heights[lines_heights.length - 1] < (format.font_table.getLineHeight() + format.leading)) {
                    lines_heights[lines_heights.length - 1] = format.font_table.getLineHeight() + format.leading;
                }
                //console.log("process word positions for textrun", tr, "textruns",  this._textRuns_words);
                w_len = this._textRuns_words[tr].start + this._textRuns_words[tr].count;
                tr_length += this._textRuns_words[tr].width;
                //console.log(this._textFieldWidth, tr_length, maxLineWidth);
            }
            this.lines_wordStartIndices[this.lines_wordStartIndices.length]
                = this._textRuns_words[this._paragraph_textRuns_indices[p]].start;
            this.lines_wordEndIndices[this.lines_wordEndIndices.length] = w_len;
            this.lines_width[this.lines_width.length] = 0;
            this.lines_numSpacesPerline[this.lines_numSpacesPerline.length] = 0;
            var lineHeightCnt = 0;
            this.lines_height[this.lines_height.length] = lines_heights[lineHeightCnt];
            lines_formats[linecnt] = format;
            for (tr = this._paragraph_textRuns_indices[p]; tr < tr_len; tr++) {
                format = this._textRuns_formats[tr];
                format.font_table.initFontSize(format.size);
                indent = format.indent;
                w_len = this._textRuns_words[tr].start + this._textRuns_words[tr].count;
                if (tr_length <= maxLineWidth || !this.wordWrap) {
                    //if(tr_length<maxLineWidth || !this.wordWrap){
                    // this must be a single textline
                    //console.log("just add to line",(tr * 4) , w_len, this.words, this._textRuns_words);
                    for (w = this._textRuns_words[tr].start; w < w_len; w += 1) {
                        var word = this.words.get(w);
                        word_width = word.width;
                        linelength += word_width;
                        this.lines_wordEndIndices[linecnt] = w + 1;
                        this.lines_width[linecnt] += word_width;
                        lines_formats[linecnt] = format;
                        if (this.chars_codes[word.start] == 32 || this.chars_codes[word.start] == 9) {
                            this.lines_numSpacesPerline[linecnt] += 1;
                        }
                    }
                }
                else {
                    //console.log("split lines");
                    word_width = 0;
                    indent = 0;
                    for (w = this._textRuns_words[tr].start; w < w_len; w += 1) {
                        var word = this.words.get(w);
                        word_width = word.width;
                        var isSpace = false;
                        if (this.chars_codes[word.start] == 32 || this.chars_codes[word.start] == 9) {
                            this.lines_numSpacesPerline[linecnt] += 1;
                            isSpace = true;
                        }
                        // (1.5* format.font_table.getCharWidth("32")) is to replicate flash behavior
                        if (isSpace
                            || (this.lines_width[linecnt] + word_width) <= (maxLineWidth - indent - (1 * format.font_table.getCharWidth('32')))
                            || this.lines_width[linecnt] == 0) {
                            this.lines_wordEndIndices[linecnt] = w + 1;
                            this.lines_width[linecnt] += word_width;
                            lines_formats[linecnt] = format;
                        }
                        else {
                            linecnt++;
                            this.lines_wordStartIndices[linecnt] = w;
                            this.lines_wordEndIndices[linecnt] = w + 1;
                            this.lines_width[linecnt] = word_width;
                            this.lines_numSpacesPerline[linecnt] = 0;
                            this.lines_height[this.lines_height.length] = lines_heights[lineHeightCnt];
                            lines_formats[linecnt] = format;
                            indent = format.indent;
                        }
                    }
                    //console.log("split lines",linecnt );
                }
                //}
            }
            lineHeightCnt++;
            linecnt++;
        }
        var offsetx = this.textOffsetX;
        var start_idx;
        var numSpaces;
        var lineHeight;
        var end_idx;
        var lineSpaceLeft;
        var l;
        var c = 0;
        var l_cnt = this.lines_wordStartIndices.length;
        var charCnt = 0;
        this._biggestLine = 0;
        this._numLines = l_cnt;
        for (l = 0; l < l_cnt; l++) {
            linelength = this.lines_width[l];
            lineHeight = this.lines_height[l];
            start_idx = this.lines_wordStartIndices[l];
            end_idx = this.lines_wordEndIndices[l];
            numSpaces = this.lines_numSpacesPerline[l];
            format = lines_formats[l];
            //console.log("numLine:", l,linelength,start_idx,end_idx,numSpaces);
            lineSpaceLeft = maxLineWidth - linelength;
            /*console.log("lineSpaceLeft", lineSpaceLeft);
            console.log("maxLineWidth", maxLineWidth);
            console.log("linelength", linelength);*/
            offsetx = this.textOffsetX + format.leftMargin + format.indent;
            if (format.align == TextFormatAlign.JUSTIFY) {
                if ((l != l_cnt - 1) && lineSpaceLeft > 0 && numSpaces > 0) {
                    // this is a textline that should be justified
                }
                if (l != 0) {
                    // only first line has indent
                    offsetx -= format.indent;
                }
            }
            else if (format.align == TextFormatAlign.CENTER) {
                if (lineSpaceLeft > 0)
                    offsetx += lineSpaceLeft / 2;
                else {
                    offsetx += 2;
                }
            }
            else if (format.align == TextFormatAlign.RIGHT) {
                if (lineSpaceLeft > 0)
                    offsetx += lineSpaceLeft - 2;
                else {
                    offsetx += 2;
                }
            }
            else if (format.align == TextFormatAlign.LEFT) {
                offsetx += 2;
            }
            var c_len = 0;
            var char_pos = offsetx;
            this.lines_start_x[l] = offsetx;
            this.lines_start_y[l] = offsety;
            this.lines_charIdx_start[l] = charCnt;
            var line_width = 0; //format.leftMargin + format.indent + format.rightMargin;
            for (w = start_idx; w < end_idx; w += 1) {
                var word = this.words.get(w);
                word.x = offsetx;
                char_pos = 0;
                start_idx = word.start;
                c_len = start_idx + word.len;
                var tf_1 = this.tf_per_char[start_idx];
                tf_1.font_table.initFontSize(tf_1.size);
                //console.log("lineHeight", lineHeight);
                //console.log("lineHeight", tf.font_table.getLineHeight()+tf.leading);
                var diff = (lineHeight) - (tf_1.font_table.getLineHeight() + tf_1.leading);
                diff = ((diff > 0) ? diff - 2 : 0);
                word.y = offsety + diff;
                for (c = start_idx; c < c_len; c++) {
                    this.char_positions_x[this.char_positions_x.length] = offsetx + char_pos;
                    this.char_positions_y[this.char_positions_y.length] = offsety + diff;
                    char_pos += this.chars_width[c];
                    charCnt++;
                }
                //console.log("word_width", char_pos, "word:'"+wordstr+"'");
                offsetx += char_pos; //this.words[w + 3];
                line_width += char_pos; //this.words[w + 3];
            }
            this.lines_charIdx_end[l] = charCnt;
            //console.log("line_width",line_width);
            offsety += lineHeight;
            /* enable for icycle:
            if(format.leading==11 && format.font_name=="DayPosterBlack"){
                offsety+=1.5;
            }*/
            if (line_width > text_width) {
                this._biggestLine = l;
                text_width = line_width;
            }
        }
        this._textWidth = text_width;
        this._textHeight = offsety;
        // if autosize is enabled, we adjust the textFieldHeight
        if (this.autoSize != TextFieldAutoSize.NONE)
            this._height = this._textHeight + 4;
        if (this._textWidth > this._width) {
            // get the max-scroll horizontal value
            start_idx = this.lines_charIdx_start[this._biggestLine];
            c = this.lines_charIdx_end[this._biggestLine];
            var maxCnt = 0;
            while (c > start_idx) {
                c--;
                maxCnt += this.chars_width[c];
                if (maxCnt > this._width) {
                    this._maxScrollH = c;
                    break;
                }
            }
        }
        if (this._textHeight > this._height) {
            // get the max-scroll vertical value
            var l_len = this.lines_height.length;
            var maxCnt = 4;
            while (l_len > 0) {
                l_len--;
                maxCnt += this.lines_height[l_len];
                if (maxCnt > this._height) {
                    this._maxScrollV = l_len + 1;
                    break;
                }
            }
        }
    };
    TextField.prototype.buildGlyphsForLabelData = function () {
        this._clearTextShapes();
        var formats = [];
        var glyphdata = [];
        var advance = [];
        var positions = [];
        var xpos = (this.staticMatrix.tx / 20);
        var ypos = (this.staticMatrix.ty / 20);
        var record;
        var lastMoveY = 0;
        for (var r = 0; r < this._labelData.records.length; r++) {
            formats[r] = new TextFormat();
            glyphdata[r] = [];
            advance[r] = [];
            record = this._labelData.records[r];
            if (record.font_table) {
                formats[r].font_table = record.font_table;
            }
            else if (r > 0) {
                formats[r].font_table = formats[r - 1].font_table;
            }
            else {
                console.log('error - no font for label');
            }
            if (record.fontHeight) {
                formats[r].size = record.fontHeight / 20;
            }
            else if (r > 0) {
                formats[r].size = formats[r - 1].size;
            }
            if (record.color) {
                //textProps.color =  this.rgbaToArgb((<any>myChild.attributes).color.nodeValue);
                formats[r].color = ColorUtils.f32_RGBA_To_f32_ARGB(record.color);
            }
            else if (r > 0) {
                formats[r].color = formats[r - 1].color;
            }
            //positions.push(this.textOffsetX+(record.moveX? record.moveX/20:0));
            //positions.push(this.textOffsetY+(record.moveY? record.moveY/20:0));
            //moveY=(record.moveY? record.moveY/20:0)-moveY;
            if (record.moveY != null) {
                ypos += (record.moveY / 20) - lastMoveY;
                lastMoveY = ypos;
            }
            else {
                //ypos+=this.staticMatrix.ty/20;
            }
            //ypos=this.textOffsetY+(record.moveY? record.moveY/20:0);
            if (record.moveX != null) {
                xpos = (this.staticMatrix.tx / 20) + (record.moveX / 20);
            }
            positions.push(xpos);
            positions.push(ypos);
            for (var e = 0; e < record.entries.length; e++) {
                glyphdata[r][e] = record.entries[e].glyphIndex;
                advance[r][e] = record.entries[e].advance / 20;
                xpos += advance[r][e];
            }
        }
        var textShape;
        // process all textRuns
        var tr = 0;
        var tr_len = formats.length;
        var lineSize;
        var text_width = 0;
        var text_height = 0;
        for (tr = 0; tr < tr_len; tr++) {
            formats[tr].font_table.initFontSize(formats[tr].size);
            lineSize = formats[tr].font_table.buildTextLineFromIndices(this, formats[tr], positions[tr * 2], positions[tr * 2 + 1], glyphdata[tr], advance[tr]);
            text_height += lineSize.y;
            text_width = (lineSize.x > text_width) ? lineSize.x : text_width;
        }
        this._textWidth = text_width;
        this._textHeight = text_height;
        //this._width=text_width+4;
        //this._height=text_height+4;
        this.targetGraphics = this._graphics;
        this.targetGraphics.clear();
        this.drawBG();
        if (this.border || (!this._background && this._type != TextFieldType.INPUT))
            this.drawBorder();
        /*
        this._graphics.clear();
        this._graphics.beginFill(0xff0000, 1);//this.background?1:0);
        this._graphics.drawRect(-1,-1,this._width, this._height);
        this._graphics.endFill();
        */
        for (var key in this.textShapes) {
            textShape = this.textShapes[key];
            var attr = new Float2Attributes(textShape.length / 2);
            var buffer = new Float32Array(attr.attributesBuffer.buffer);
            var offset = 0;
            for (var _i = 0, _a = textShape.verts; _i < _a.length; _i++) {
                var chunk = _a[_i];
                buffer.set(chunk, offset);
                offset += chunk.length;
            }
            textShape.elements = new TriangleElements();
            textShape.elements.setPositions(attr);
            textShape.elements.invalidate();
            textShape.shape = this.targetGraphics.addShape(Shape.getShape(textShape.elements));
            // has BUG for QWOP, temporarily enable it (default)
            textShape.shape.deepHitCheck = true;
            textShape.shape.usages++;
            var sampler = new ImageSampler();
            textShape.shape.style = new Style();
            if (textShape.format.material && this._textColor == 0) {
                textShape.shape.material = this._textFormats[0].material;
                textShape.shape.style.addSamplerAt(sampler, textShape.shape.material.getTextureAt(0));
                textShape.shape.material.animateUVs = true;
                textShape.shape.style.uvMatrix =
                    new Matrix(0, 0, 0, 0, textShape.format.uv_values[0], textShape.format.uv_values[1]);
            }
            else {
                var color = this.getTextColorForTextFormat(textShape.format);
                var alpha = ColorUtils.float32ColorToARGB(color)[0];
                if (alpha == 0) {
                    alpha = 255;
                }
                var obj = MaterialManager.getMaterialForColor(color, alpha / 255);
                textShape.shape.material = obj.material;
                if (obj.colorPos) {
                    textShape.shape.style.addSamplerAt(sampler, textShape.shape.material.getTextureAt(0));
                    textShape.shape.material.animateUVs = true;
                    textShape.shape.style.uvMatrix = new Matrix(0, 0, 0, 0, obj.colorPos.x, obj.colorPos.y);
                }
            }
        }
    };
    TextField.prototype.buildGlyphs = function () {
        var tr_formats = this._textRuns_formats;
        var tr_words = this._textRuns_words;
        var tr_len = tr_formats.length;
        if (this._textShapesDirty)
            this._clearTextShapes();
        for (var tr = 0; tr < tr_len; tr++) {
            var run = tr_words[tr];
            if (run.count == 0) {
                continue;
                // } else if (tr_words[(tr * 4)] + tr_words[(tr * 4) + 1] * 5 <= this._words_amount_prev) {
                // continue;
            }
            tr_formats[tr].font_table.initFontSize(tr_formats[tr].size);
            if (Settings.USE_UNSAFE_FNT) {
                tr_formats[tr].font_table.generateFNTData(null);
            }
            tr_formats[tr].font_table.fillTextRun(this, tr_formats[tr], run.start, run.count);
        }
        var textShape;
        for (var key in this.textShapes) {
            textShape = this.textShapes[key];
            if (textShape.length === 0) {
                continue;
            }
            var attr = new Float2Attributes(textShape.length / 2);
            var pos = new Float32Array(attr.attributesBuffer.buffer);
            var uvAttr = textShape.hasUV ? new Float2Attributes(textShape.length / 2) : null;
            var uvs = uvAttr ? new Float32Array(uvAttr.attributesBuffer.buffer) : null;
            //console.log('Build shape size:', textShape.length);
            var offset = 0;
            for (var i = 0; i < textShape.verts.length; i++) {
                var chunk = textShape.verts[i];
                pos.set(chunk, offset);
                if (uvs) {
                    uvs.set(textShape.uvs[i], offset);
                }
                offset += chunk.length;
            }
            // hack, merge buffer in single
            // this helps to avoid a lot of set in incremental filling
            // but anyway is slow
            textShape.length = 0;
            textShape.addChunk(pos, uvs);
            if (textShape.shape) {
                var element = textShape.shape.elements;
                // there are bug with attribute buffer, when add attr to exist
                element.setPositions(pos);
                if (uvs) {
                    element.setUVs(uvs);
                }
                element.invalidate();
                attr.dispose();
                if (uvAttr) {
                    uvAttr.dispose();
                }
                continue;
            }
            var color = this.getTextColorForTextFormat(textShape.format);
            var alpha = ColorUtils.float32ColorToARGB(color)[0] || 255;
            textShape.elements = new TriangleElements();
            textShape.elements.setPositions(attr);
            if (uvAttr) {
                textShape.elements.setUVs(uvAttr);
            }
            textShape.elements.invalidate();
            textShape.shape = Shape.getShape(textShape.elements);
            // has BUG for QWOP, temporarily enable it (default)
            textShape.shape.deepHitCheck = true;
            textShape.shape.usages++;
            var sampler = new ImageSampler(false, true, true);
            textShape.shape.style = new Style();
            if (textShape.fntMaterial) {
                // 	used by FNT fonts
                textShape.shape.material = textShape.fntMaterial;
                textShape.shape.style.addSamplerAt(sampler, textShape.shape.material.getTextureAt(0));
                //(<MaterialBase> textShape.shape.material).colorTransform=new ColorTransform();
                //(<MaterialBase> textShape.shape.material).colorTransform.color=color;
            }
            else if (textShape.format.material && this._textColor == 0) {
                // 	used for textfields loaded from awd.
                //	the material on the format uses textureAtlas from awd
                textShape.shape.material = this._textFormats[0].material;
                textShape.shape.style.addSamplerAt(sampler, textShape.shape.material.getTextureAt(0));
                textShape.shape.material.animateUVs = true;
                textShape.shape.style.uvMatrix =
                    new Matrix(0, 0, 0, 0, textShape.format.uv_values[0], textShape.format.uv_values[1]);
            }
            else {
                // 	used by runtime textureatlas.
                //	(standart for dynamic created text and text loaded from swf)
                var obj = MaterialManager.getMaterialForColor(color, alpha / 255);
                textShape.shape.material = obj.material;
                if (obj.colorPos) {
                    textShape.shape.style.addSamplerAt(sampler, textShape.shape.material.getTextureAt(0));
                    textShape.shape.material.animateUVs = true;
                    textShape.shape.style.uvMatrix = new Matrix(0, 0, 0, 0, obj.colorPos.x, obj.colorPos.y);
                }
            }
        }
    };
    TextField.prototype.buildShapes = function () {
        if (!this.targetGraphics) {
            this.targetGraphics = this._graphics;
        }
        else {
            this._graphics.clear();
        }
        this.targetGraphics.clear();
        this.drawBG();
        if (this._border || !this._background) {
            this.drawBorder();
        }
        this.drawSelectionGraphics();
        if (this.bgShapeSelect && this.isInFocus && this.showSelection) {
            this.targetGraphics.addShape(this.bgShapeSelect);
        }
        var textShape;
        for (var key in this.textShapes) {
            textShape = this.textShapes[key];
            if (!textShape.shape) {
                continue;
            }
            this.targetGraphics.addShape(textShape.shape);
        }
        if (this.type === TextFieldType.INPUT && this.isInFocus && this.cursorShape && !this.cursorBlinking) {
            this.targetGraphics.addShape(this.cursorShape);
        }
    };
    /**
     * Appends the string specified by the <code>newText</code> parameter to the
     * end of the text of the text field. This method is more efficient than an
     * addition assignment(<code>+=</code>) on a <code>text</code> property
     * (such as <code>someTextField.text += moreText</code>), particularly for a
     * text field that contains a significant amount of content.
     *
     * @param newText The string to append to the existing text.
     */
    TextField.prototype.appendText = function (newText) {
        // append zero lenght text
        if (!newText.length) {
            return;
        }
        this._iText += newText;
        this._textDirty = true;
        this.invalidate();
    };
    /**
     * *tells the Textfield that a paragraph is defined completly.
     * e.g. the textfield will start a new line for future added text.
     */
    TextField.prototype.closeParagraph = function () {
        this._iText += '\n';
        this._textDirty = true;
        this.invalidate();
    };
    /**
     * Returns a rectangle that is the bounding box of the character.
     *
     * @param charIndex The zero-based index value for the character(for
     *                  example, the first position is 0, the second position is
     *                  1, and so on).
     * @return A rectangle with <code>x</code> and <code>y</code> minimum and
     *         maximum values defining the bounding box of the character.
     */
    TextField.prototype.getCharBoundaries = function (charIndex) {
        var charBounds = new Rectangle();
        if (charIndex >= this.char_positions_x.length) {
            return charBounds;
        }
        charBounds.x = this.char_positions_x[charIndex];
        charBounds.width = this.chars_width[charIndex];
        charBounds.y = this.char_positions_y[charIndex];
        charBounds.height = 10; // @todo
        return charBounds;
    };
    /**
     * Returns the zero-based index value of the character at the point specified
     * by the <code>x</code> and <code>y</code> parameters.
     *
     * @param x The <i>x</i> coordinate of the character.
     * @param y The <i>y</i> coordinate of the character.
     * @return The zero-based index value of the character(for example, the
     *         first position is 0, the second position is 1, and so on). Returns
     *         -1 if the point is not over any character.
     */
    TextField.prototype.getCharIndexAtPoint = function (x, y, lineIdx) {
        if (lineIdx === void 0) { lineIdx = -1; }
        if (lineIdx < 0) {
            lineIdx = this.getLineIndexAtPoint(x, y);
        }
        var startIdx = this.lines_charIdx_start[lineIdx];
        var endIdx = this.lines_charIdx_end[lineIdx];
        for (var i = startIdx; i < endIdx; i++) {
            if (x >= this.char_positions_x[i]) {
                if (x <= this.char_positions_x[i] + this.chars_width[i] / 2) {
                    return i;
                }
                else if (x <= this.char_positions_x[i] + this.chars_width[i]) {
                    return i + 1;
                }
            }
        }
        return -1;
    };
    /**
     * Given a character index, returns the index of the first character in the
     * same paragraph.
     *
     * @param charIndex The zero-based index value of the character(for example,
     *                  the first character is 0, the second character is 1, and
     *                  so on).
     * @return The zero-based index value of the first character in the same
     *         paragraph.
     * @throws RangeError The character index specified is out of range.
     */
    TextField.prototype.getFirstCharInParagraph = function (charIndex /*int*/) {
        console.log('Textfield.getFirstCharInParagraph() not implemented');
        return this._firstCharInParagraph;
    };
    /**
     * Returns a DisplayObject reference for the given <code>id</code>, for an
     * image or SWF file that has been added to an HTML-formatted text field by
     * using an <code><img></code> tag. The <code><img></code> tag is in the
     * following format:
     *
     * <p><pre xml:space="preserve"><code> <img src = 'filename.jpg' id =
     * 'instanceName' ></code></pre></p>
     *
     * @param id The <code>id</code> to match(in the <code>id</code> attribute
     *           of the <code><img></code> tag).
     * @return The display object corresponding to the image or SWF file with the
     *         matching <code>id</code> attribute in the <code><img></code> tag
     *         of the text field. For media loaded from an external source, this
     *         object is a Loader object, and, once loaded, the media object is a
     *         child of that Loader object. For media embedded in the SWF file,
     *         it is the loaded object. If no <code><img></code> tag with the
     *         matching <code>id</code> exists, the method returns
     *         <code>null</code>.
     */
    TextField.prototype.getImageReference = function (id) {
        console.log('TextField.getImageReference() not implemented');
        return this._imageReference;
    };
    /**
     * Returns the zero-based index value of the line at the point specified by
     * the <code>x</code> and <code>y</code> parameters.
     *
     * @param x The <i>x</i> coordinate of the line.
     * @param y The <i>y</i> coordinate of the line.
     * @return The zero-based index value of the line(for example, the first
     *         line is 0, the second line is 1, and so on). Returns -1 if the
     *         point is not over any line.
     */
    TextField.prototype.getLineIndexAtPoint = function (x, y) {
        var len = this.lines_start_y.length;
        for (var i = 0; i < len - 1; i++) {
            if (y >= this.lines_start_y[i] && y <= this.lines_start_y[i + 1])
                return i;
        }
        // no line found. it must be the last
        if (y >= this.lines_start_y[len - 1])
            return len - 1;
        return 0;
    };
    /**
     * Returns the zero-based index value of the line containing the character
     * specified by the <code>charIndex</code> parameter.
     *
     * @param charIndex The zero-based index value of the character(for example,
     *                  the first character is 0, the second character is 1, and
     *                  so on).
     * @return The zero-based index value of the line.
     * @throws RangeError The character index specified is out of range.
     */
    TextField.prototype.getLineIndexOfChar = function (charIndex /*int*/) {
        this.buildParagraphs();
        var len = this.lines_charIdx_start.length - 1;
        for (var i = void 0; i < len; i++) {
            if (charIndex >= this.lines_charIdx_start[i] && charIndex <= this.lines_charIdx_end[i + 1])
                return i;
        }
        // no line found. it must be the last
        return len;
    };
    /**
     * Returns the number of characters in a specific text line.
     *
     * @param lineIndex The line number for which you want the length.
     * @return The number of characters in the line.
     * @throws RangeError The line number specified is out of range.
     */
    TextField.prototype.getLineLength = function (lineIndex /*int*/) {
        if (this.lines_width.length == 0) {
            return 0;
        }
        if (lineIndex >= this.lines_width.length)
            return this.lines_width[this.lines_width.length - 1];
        return this.lines_width[lineIndex];
    };
    /**
     * Returns metrics information about a given text line.
     *
     * @param lineIndex The line number for which you want metrics information.
     * @return A TextLineMetrics object.
     * @throws RangeError The line number specified is out of range.
     */
    TextField.prototype.getLineMetrics = function (lineIndex /*int*/) {
        var newLineMetrics = new TextLineMetrics();
        if (!this.lines_width.length) {
            return newLineMetrics;
        }
        // should throw error!
        if (lineIndex >= this.lines_width.length) {
            lineIndex = this.lines_width.length - 1;
        }
        newLineMetrics.x = this.lines_start_x[lineIndex];
        newLineMetrics.width = this.lines_width[lineIndex];
        newLineMetrics.height = this.lines_height[lineIndex];
        //newLineMetrics.ascent=
        //newLineMetrics.descent
        //newLineMetrics.height
        //newLineMetrics.leading
        return newLineMetrics;
    };
    /**
     * Returns the character index of the first character in the line that the
     * <code>lineIndex</code> parameter specifies.
     *
     * @param lineIndex The zero-based index value of the line(for example, the
     *                  first line is 0, the second line is 1, and so on).
     * @return The zero-based index value of the first character in the line.
     * @throws RangeError The line number specified is out of range.
     */
    TextField.prototype.getLineOffset = function (lineIndex /*int*/) {
        if (this.lines_charIdx_start.length == 0) {
            return 0;
        }
        if (lineIndex >= this.lines_charIdx_start.length)
            return this.lines_charIdx_start[this.lines_charIdx_start.length - 1];
        return this.lines_charIdx_start[lineIndex];
    };
    /**
     * Returns the text of the line specified by the <code>lineIndex</code>
     * parameter.
     *
     * @param lineIndex The zero-based index value of the line(for example, the
     *                  first line is 0, the second line is 1, and so on).
     * @return The text string contained in the specified line.
     * @throws RangeError The line number specified is out of range.
     */
    TextField.prototype.getLineText = function (lineIndex /*int*/) {
        if (this.lines_charIdx_start.length == 0) {
            return '';
        }
        if (lineIndex >= this.lines_width.length)
            lineIndex = this.lines_width.length - 1;
        return this._iText.slice(this.lines_charIdx_start[lineIndex], this.lines_charIdx_end[lineIndex]);
    };
    /**
     * Given a character index, returns the length of the paragraph containing
     * the given character. The length is relative to the first character in the
     * paragraph(as returned by <code>getFirstCharInParagraph()</code>), not to
     * the character index passed in.
     *
     * @param charIndex The zero-based index value of the character(for example,
     *                  the first character is 0, the second character is 1, and
     *                  so on).
     * @return Returns the number of characters in the paragraph.
     * @throws RangeError The character index specified is out of range.
     */
    TextField.prototype.getParagraphLength = function (charIndex /*int*/) {
        return this._paragraphLength;
    };
    /**
     * Returns a TextFormat object that contains formatting information for the
     * range of text that the <code>beginIndex</code> and <code>endIndex</code>
     * parameters specify. Only properties that are common to the entire text
     * specified are set in the resulting TextFormat object. Any property that is
     * <i>mixed</i>, meaning that it has different values at different points in
     * the text, has a value of <code>null</code>.
     *
     * <p>If you do not specify values for these parameters, this method is
     * applied to all the text in the text field. </p>
     *
     * <p>The following table describes three possible usages:</p>
     *
     * @return The TextFormat object that represents the formatting properties
     *         for the specified text.
     * @throws RangeError The <code>beginIndex</code> or <code>endIndex</code>
     *                    specified is out of range.
     */
    TextField.prototype.getTextFormat = function (beginIndex, endIndex) {
        var _a;
        if (beginIndex === void 0) { beginIndex = -1; }
        if (endIndex === void 0) { endIndex = -1; }
        if (!this.tf_per_char || !this.tf_per_char.length) {
            return ((_a = this._textFormats[0]) === null || _a === void 0 ? void 0 : _a.clone()) || new TextFormat();
        }
        if (beginIndex < 0)
            beginIndex = 0;
        if (endIndex > this.tf_per_char.length) {
            endIndex = this.tf_per_char.length;
        }
        var newTextFormat = this.tf_per_char[beginIndex].clone();
        var lastFormat = this.tf_per_char[beginIndex];
        for (var i = beginIndex + 1; i < endIndex; i++) {
            if (this.tf_per_char[i] == lastFormat) {
                continue;
            }
            newTextFormat.mergeFormat(this.tf_per_char[i]);
            lastFormat = this.tf_per_char[i];
        }
        return newTextFormat;
    };
    /**
     * Replaces the current selection with the contents of the <code>value</code>
     * parameter. The text is inserted at the position of the current selection,
     * using the current default character format and default paragraph format.
     * The text is not treated as HTML.
     *
     * <p>You can use the <code>replaceSelectedText()</code> method to insert and
     * delete text without disrupting the character and paragraph formatting of
     * the rest of the text.</p>
     *
     * <p><b>Note:</b> This method does not work if a style sheet is applied to
     * the text field.</p>
     *
     * @param value The string to replace the currently selected text.
     * @throws Error This method cannot be used on a text field with a style
     *               sheet.
     */
    TextField.prototype.replaceSelectedText = function (value) {
        return this._insertNewText(value);
        // this is wrong implemementation, how i see,
        // because it not right invalidate a text;
        /*
        let selectionStart: number = this._selectionBeginIndex;
        let selectionEnd: number = this._selectionEndIndex;
        if (selectionEnd != selectionStart) {
            if (selectionEnd < selectionStart) {
                selectionStart = this._selectionEndIndex;
                selectionEnd = this._selectionBeginIndex;
            }
            const textBeforeCursor: string = this._iText.slice(0, selectionStart - 1);
            const textAfterCursor: string = this._iText.slice(selectionEnd, this._iText.length);
            this.text = textBeforeCursor + value + textAfterCursor;
            this._selectionBeginIndex = selectionStart;
            this._selectionEndIndex = this._selectionBeginIndex + value.length;
            return;
        }
        const textBeforeCursor: string = this._iText.slice(0, selectionStart);
        const textAfterCursor: string = this._iText.slice(selectionEnd, this._iText.length);
        this.text = textBeforeCursor + value + textAfterCursor;
        this._selectionBeginIndex = selectionStart + 1;
        this._selectionEndIndex = this._selectionBeginIndex;
        */
    };
    /**
     * Replaces the range of characters that the <code>beginIndex</code> and
     * <code>endIndex</code> parameters specify with the contents of the
     * <code>newText</code> parameter. As designed, the text from
     * <code>beginIndex</code> to <code>endIndex-1</code> is replaced.
     *
     * <p><b>Note:</b> This method does not work if a style sheet is applied to
     * the text field.</p>
     *
     * @param beginIndex The zero-based index value for the start position of the
     *                   replacement range.
     * @param endIndex   The zero-based index position of the first character
     *                   after the desired text span.
     * @param newText    The text to use to replace the specified range of
     *                   characters.
     * @throws Error This method cannot be used on a text field with a style
     *               sheet.
     */
    TextField.prototype.replaceText = function (beginIndex /*int*/, endIndex /*int*/, newText) {
        var textBeforeCursor = this._iTextWoLineBreaks.slice(0, beginIndex - 1);
        var textAfterCursor = this._iTextWoLineBreaks.slice(endIndex, this._iTextWoLineBreaks.length);
        this.text = textBeforeCursor + newText + textAfterCursor;
        this._selectionEndIndex = this._selectionBeginIndex + newText.length;
    };
    /**
     * Sets as selected the text designated by the index values of the first and
     * last characters, which are specified with the <code>beginIndex</code> and
     * <code>endIndex</code> parameters. If the two parameter values are the
     * same, this method sets the insertion point, as if you set the
     * <code>caretIndex</code> property.
     *
     * @param beginIndex The zero-based index value of the first character in the
     *                   selection(for example, the first character is 0, the
     *                   second character is 1, and so on).
     * @param endIndex   The zero-based index value of the last character in the
     *                   selection.
     */
    TextField.prototype.setSelection = function (beginIndex /*int*/, endIndex /*int*/) {
        if (this._selectionBeginIndex == beginIndex && this._selectionBeginIndex == endIndex)
            return;
        this._selectionBeginIndex = beginIndex;
        this._selectionEndIndex = endIndex;
        this._glyphsDirty = true;
        this.reConstruct();
        this.drawSelectionGraphics();
    };
    /**
     * Applies the text formatting that the <code>format</code> parameter
     * specifies to the specified text in a text field. The value of
     * <code>format</code> must be a TextFormat object that specifies the desired
     * text formatting changes. Only the non-null properties of
     * <code>format</code> are applied to the text field. Any property of
     * <code>format</code> that is set to <code>null</code> is not applied. By
     * default, all of the properties of a newly created TextFormat object are
     * set to <code>null</code>.
     *
     * <p><b>Note:</b> This method does not work if a style sheet is applied to
     * the text field.</p>
     *
     * <p>The <code>setTextFormat()</code> method changes the text formatting
     * applied to a range of characters or to the entire body of text in a text
     * field. To apply the properties of format to all text in the text field, do
     * not specify values for <code>beginIndex</code> and <code>endIndex</code>.
     * To apply the properties of the format to a range of text, specify values
     * for the <code>beginIndex</code> and the <code>endIndex</code> parameters.
     * You can use the <code>length</code> property to determine the index
     * values.</p>
     *
     * <p>The two types of formatting information in a TextFormat object are
     * character level formatting and paragraph level formatting. Each character
     * in a text field can have its own character formatting settings, such as
     * font name, font size, bold, and italic.</p>
     *
     * <p>For paragraphs, the first character of the paragraph is examined for
     * the paragraph formatting settings for the entire paragraph. Examples of
     * paragraph formatting settings are left margin, right margin, and
     * indentation.</p>
     *
     * <p>Any text inserted manually by the user, or replaced by the
     * <code>replaceSelectedText()</code> method, receives the default text field
     * formatting for new text, and not the formatting specified for the text
     * insertion point. To set the default formatting for new text, use
     * <code>defaultTextFormat</code>.</p>
     *
     * @param format A TextFormat object that contains character and paragraph
     *               formatting information.
     * @throws Error      This method cannot be used on a text field with a style
     *                    sheet.
     * @throws RangeError The <code>beginIndex</code> or <code>endIndex</code>
     *                    specified is out of range.
     */
    TextField.prototype.setTextFormat = function (format, beginIndex, endIndex) {
        if (beginIndex === void 0) { beginIndex = -1; }
        if (endIndex === void 0) { endIndex = -1; }
        /**
         *  this should only effect existing text
         *  if no text exist, this function does nothing
         *
         *  this means that we only want to act on the existing textFormats list,
         *  never on the textFormat property directly
         * */
        if (!format || this._iTextWoLineBreaks.length == 0)
            return;
        if ((beginIndex == -1 && endIndex == -1)
            || (beginIndex == 0 && endIndex == -1)
            || ((beginIndex == -1 || beginIndex == 0)
                && endIndex >= this._iTextWoLineBreaks.length)) {
            var len = this._textFormats.length;
            if (this._textFormats.length === 1 && this._textFormats[0].equal(format)) {
                return;
            }
            // easy: apply the format to all formats in the list
            for (var i_1 = 0; i_1 < len; i_1++) {
                this._textFormats[i_1] = this._textFormats[i_1].clone();
                format.applyToFormat(this._textFormats[i_1]);
            }
            this._textDirty = true;
            this._textShapesDirty = true;
            return;
        }
        // todo: the above conditions are a hack to get it working for a AVM1 lesson.
        // below is the code that should actually do the job more, but could not get it to work 100% yet
        //console.log("\n\nadd format", this.id, this._iText, beginIndex, endIndex, format.color);
        //console.log("this._textFormats", this._textFormats, this._textFormatsIdx);
        var i = 0;
        /**
         * _textformatsIdx list should always be ordered numeric
         * todo: should this be verified here, or is it already taken care of ?
         */
        var newFormatsTextFormats = [];
        var newFormatsTextFormatsIdx = [];
        var oldStartIdx = 0;
        var oldEndIdx = -1;
        var oldFormat;
        var formatLen = this._textFormats.length;
        var textLen = this._iTextWoLineBreaks.length;
        if (beginIndex == -1)
            beginIndex = 0;
        if (endIndex == -1)
            endIndex = textLen;
        if (endIndex < beginIndex) {
            var tmp = endIndex;
            endIndex = beginIndex;
            beginIndex = tmp;
        }
        if (endIndex == beginIndex)
            endIndex++;
        //console.log("check formats");
        for (var i_2 = 0; i_2 < formatLen; i_2++) {
            if (i_2 > 0)
                oldStartIdx = oldEndIdx;
            oldEndIdx = this._textFormatsIdx[i_2];
            oldFormat = this._textFormats[i_2];
            //console.log("oldFormat", oldStartIdx, oldEndIdx);
            //console.log("check formats", oldStartIdx, oldEndIdx, beginIndex, endIndex);
            if (oldStartIdx <= beginIndex && oldEndIdx > beginIndex) {
                // we have a interset in the range.
                //console.log("intersects");
                // we have a bit of text that should remain the original format
                if (oldStartIdx < beginIndex) {
                    newFormatsTextFormats.push(oldFormat);
                    newFormatsTextFormatsIdx.push(beginIndex);
                    //console.log("add old format", beginIndex);
                }
                while (oldEndIdx < endIndex) {
                    //console.log("add new merged format", oldEndIdx);
                    var newFormat = this._textFormats[i_2].clone();
                    format.applyToFormat(newFormat);
                    newFormatsTextFormats.push(newFormat);
                    newFormatsTextFormatsIdx.push(oldEndIdx);
                    i_2++;
                    if (i_2 < formatLen) {
                        oldEndIdx = this._textFormatsIdx[i_2];
                        oldFormat = this._textFormats[i_2];
                    }
                    else {
                        oldEndIdx = endIndex + 1;
                    }
                }
                if (oldEndIdx == endIndex) {
                    //console.log("add new format rest", endIndex);
                    var newFormat = oldFormat.clone();
                    format.applyToFormat(newFormat);
                    newFormatsTextFormats.push(newFormat);
                    newFormatsTextFormatsIdx.push(endIndex);
                }
                if (oldEndIdx > endIndex) {
                    //console.log("add new format rest", endIndex);
                    var newFormat = oldFormat.clone();
                    format.applyToFormat(newFormat);
                    newFormatsTextFormats.push(newFormat);
                    newFormatsTextFormatsIdx.push(endIndex);
                    //console.log("add old format rest", oldEndIdx);
                    newFormatsTextFormats.push(oldFormat);
                    newFormatsTextFormatsIdx.push(oldEndIdx);
                }
            }
            else {
                //console.log("outside of new range. just add it", oldStartIdx, oldEndIdx);
                // outside of new range. just add it
                newFormatsTextFormats.push(this._textFormats[i_2]);
                newFormatsTextFormatsIdx.push(this._textFormatsIdx[i_2]);
            }
        }
        //console.log("new formats");
        this._textFormats.length = 0;
        this._textFormatsIdx.length = 0;
        for (i = 0; i < newFormatsTextFormats.length; i++) {
            //console.log("new formats ", newFormatsTextFormatsIdx[i], newFormatsTextFormats[i]);
            this._textFormats[i] = newFormatsTextFormats[i];
            this._textFormatsIdx[i] = newFormatsTextFormatsIdx[i];
        }
        this._textDirty = true;
        this._textShapesDirty = true;
    };
    /**
     * Returns true if an embedded font is available with the specified
     * <code>fontName</code> and <code>fontStyle</code> where
     * <code>Font.fontType</code> is <code>flash.text.FontType.EMBEDDED</code>.
     * Starting with Flash Player 10, two kinds of embedded fonts can appear in a
     * SWF file. Normal embedded fonts are only used with TextField objects. CFF
     * embedded fonts are only used with the flash.text.engine classes. The two
     * types are distinguished by the <code>fontType</code> property of the
     * <code>Font</code> class, as returned by the <code>enumerateFonts()</code>
     * function.
     *
     * <p>TextField cannot use a font of type <code>EMBEDDED_CFF</code>. If
     * <code>embedFonts</code> is set to <code>true</code> and the only font
     * available at run time with the specified name and style is of type
     * <code>EMBEDDED_CFF</code>, Flash Player fails to render the text, as if no
     * embedded font were available with the specified name and style.</p>
     *
     * <p>If both <code>EMBEDDED</code> and <code>EMBEDDED_CFF</code> fonts are
     * available with the same name and style, the <code>EMBEDDED</code> font is
     * selected and text renders with the <code>EMBEDDED</code> font.</p>
     *
     * @param fontName  The name of the embedded font to check.
     * @param fontStyle Specifies the font style to check. Use
     *                  <code>flash.text.FontStyle</code>
     * @return <code>true</code> if a compatible embedded font is available,
     *         otherwise <code>false</code>.
     * @throws ArgumentError The <code>fontStyle</code> specified is not a member
     *                       of <code>flash.text.FontStyle</code>.
     */
    TextField.isFontCompatible = function (fontName, fontStyle) {
        return false;
    };
    TextField.prototype.onKey = function (e) {
        var keyEvent = e;
        this.addChar(keyEvent.char, keyEvent.isShift, keyEvent.isCTRL, keyEvent.isAlt);
        //console.log("textfield.onKey this.text", this.text);
    };
    TextField.prototype.addCharCode = function (charCode) {
        this.addChar(String.fromCharCode(charCode));
    };
    TextField.prototype.deleteSelectedText = function (deleteMode) {
        if (deleteMode === void 0) { deleteMode = 'Backspace'; }
        if (this.text.length == 0)
            return;
        if (this._selectionBeginIndex != this._selectionEndIndex) {
            var textBeforeCursor = (this._selectionBeginIndex != 0) ? this._iText.slice(0, this._selectionBeginIndex) : '';
            var textAfterCursor = (this._selectionEndIndex < this._iText.length) ?
                this._iText.slice(this._selectionEndIndex, this._iText.length) : '';
            this.text = textBeforeCursor + textAfterCursor;
            this._selectionEndIndex = this._selectionBeginIndex;
            return;
        }
        if (deleteMode == 'Backspace') {
            if (this._selectionBeginIndex == 0) {
                return;
            }
            var textBeforeCursor = this._iText.slice(0, this._selectionBeginIndex - 1);
            var textAfterCursor = this._iText.slice(this._selectionEndIndex, this._iText.length);
            this.text = textBeforeCursor + textAfterCursor;
            this._selectionBeginIndex -= 1;
            this._selectionEndIndex = this._selectionBeginIndex;
        }
        else if (deleteMode == 'Delete') {
            var textBeforeCursor = this._iText.slice(0, this._selectionBeginIndex);
            var textAfterCursor = this._iText.slice(this._selectionEndIndex + 1, this._iText.length);
            this.text = textBeforeCursor + textAfterCursor;
            this._selectionEndIndex = this._selectionBeginIndex;
        }
    };
    TextField.prototype.addChar = function (char, isShift, isCTRL, isAlt) {
        var _this = this;
        if (isShift === void 0) { isShift = false; }
        if (isCTRL === void 0) { isCTRL = false; }
        if (isAlt === void 0) { isAlt = false; }
        var changed = false;
        var oldText = this._iText;
        if (!this._selectionBeginIndex) {
            this._selectionBeginIndex = 0;
        }
        if (!this._selectionEndIndex) {
            this._selectionEndIndex = 0;
        }
        if (this._selectionEndIndex < this._selectionBeginIndex) {
            var tmpStart = this._selectionEndIndex;
            this._selectionEndIndex = this._selectionBeginIndex;
            this._selectionBeginIndex = tmpStart;
        }
        if (!isAlt && !isCTRL) {
            if (char == 'Backspace' || char == 'Delete') {
                this.deleteSelectedText(char);
                changed = true;
            }
            else if (char == 'ArrowRight') {
                if (!isShift && this._selectionEndIndex != this._selectionBeginIndex) {
                    if (this._selectionEndIndex > this._selectionBeginIndex) {
                        this._selectionBeginIndex = this._selectionEndIndex;
                    }
                    else {
                        this._selectionEndIndex = this._selectionBeginIndex;
                    }
                }
                else {
                    if (this._selectionEndIndex > this._selectionBeginIndex) {
                        this._selectionEndIndex += 1;
                        if (!isShift)
                            this._selectionBeginIndex = this._selectionEndIndex;
                    }
                    else {
                        this._selectionBeginIndex += 1;
                        if (!isShift)
                            this._selectionEndIndex = this._selectionBeginIndex;
                    }
                }
                changed = true;
                //this._selectionEndIndex=this._selectionBeginIndex;
            }
            else if (char == 'ArrowLeft') {
                if (!isShift && this._selectionEndIndex != this._selectionBeginIndex) {
                    if (this._selectionEndIndex > this._selectionBeginIndex)
                        this._selectionEndIndex = this._selectionBeginIndex;
                    else
                        this._selectionBeginIndex = this._selectionEndIndex;
                }
                else {
                    if (this._selectionEndIndex > this._selectionBeginIndex) {
                        this._selectionBeginIndex -= 1;
                        if (!isShift)
                            this._selectionEndIndex = this._selectionBeginIndex;
                    }
                    else {
                        this._selectionEndIndex -= 1;
                        if (!isShift)
                            this._selectionBeginIndex = this._selectionEndIndex;
                    }
                }
                changed = true;
                //this._selectionEndIndex=this._selectionBeginIndex;
            }
            else if (char == 'Enter' && this.multiline) {
                this._insertNewText('\n');
                changed = true;
            }
            else if (char.length == 1) {
                if (this._restrictRegex) {
                    var chartest1 = char.replace(this._restrictRegex, '');
                    if (chartest1.length < char.length) {
                        var chartest2 = char.toUpperCase().replace(this._restrictRegex, '');
                        var chartest3 = char.toLowerCase().replace(this._restrictRegex, '');
                        char = chartest2;
                        if (chartest2.length < chartest3.length) {
                            char = chartest3;
                        }
                        if (chartest1.length > char.length) {
                            char = chartest1;
                        }
                    }
                    if (char == '')
                        return;
                }
                if (this._newTextFormat.font_table) {
                    var table = this._newTextFormat.font_table;
                    var symbol = char.charCodeAt(0).toString();
                    var exist = false;
                    exist = (exist || table.hasChar(symbol));
                    exist = (exist || table.hasChar(symbol.toLowerCase()));
                    exist = (exist || table.hasChar(symbol.toUpperCase()));
                    if (!exist) {
                        console.log('Char not found', symbol);
                        return;
                    }
                }
                this._insertNewText(char);
                changed = true;
            }
            else if (char.length > 1) {
                console.log('invalid keyboard input: ', char);
            }
            if (changed) {
                this._glyphsDirty = true;
                //this.reConstruct();
                //this.drawSelectionGraphics();
                this.invalidate();
                if (oldText !== this._iText) {
                    this.dispatchEvent(TextField._onChangedEvent);
                }
            }
        }
        else {
            /**
            * @todo THIS IS SHIT!!! This won't working on MacOS, because it use another Ctrl + V/C
            * @see `enableInput` setter for right implementation
            */
            if (isCTRL && !isShift && char === 'v') {
                // paste
                // try do it
                if (navigator.clipboard.readText) {
                    navigator.clipboard.readText()
                        .then(function (e) {
                        _this._insertNewText(e);
                    })
                        .catch(function (e) {
                        console.warn('[TextField] Can\'t paste text:', e.message);
                    });
                }
            }
        }
        if (char && char.length > 0 && this.adapter != this) {
            var charCode = void 0;
            switch (char) {
                case 'Backspace':
                    charCode = 8;
                    break;
                case 'Delete':
                    charCode = 46;
                    break;
                case 'ArrowRight':
                    charCode = 39;
                    break;
                case 'ArrowLeft':
                    charCode = 37;
                    break;
                case '.':
                    charCode = 189;
                    break;
                default:
                    charCode = char.charCodeAt(0);
                    break;
            }
            //console.log("dispatchKeyEvent");
            this.adapter.dispatchKeyEvent(charCode, isShift, isCTRL, isAlt);
        }
    };
    TextField.prototype._insertNewText = function (newText) {
        if (this._selectionBeginIndex != this._selectionEndIndex) {
            var textBeforeCursor = this._iText.slice(0, this._selectionBeginIndex);
            var textAfterCursor = this._iText.slice(this._selectionEndIndex, this._iText.length);
            if (this.maxChars > 0
                && (textBeforeCursor.length + textAfterCursor.length + newText.length) > this.maxChars) {
                var maxNewChars = this.maxChars - textBeforeCursor.length + textAfterCursor.length;
                if (maxNewChars > 0) {
                    newText = newText.slice(0, maxNewChars);
                }
            }
            this.text = textBeforeCursor + newText + textAfterCursor;
            this._textShapesDirty = true;
            this._selectionBeginIndex += 1;
            this._selectionEndIndex = this._selectionBeginIndex;
        }
        else {
            if (this.maxChars > 0 && this._iText.length >= this.maxChars) {
                // do nothing
            }
            else {
                var textBeforeCursor = this._iText.slice(0, this._selectionBeginIndex);
                var textAfterCursor = this._iText.slice(this._selectionEndIndex, this._iText.length);
                this.text = textBeforeCursor + newText + textAfterCursor;
                this._textShapesDirty = true;
                this._selectionBeginIndex += 1;
                this._selectionEndIndex = this._selectionBeginIndex;
            }
        }
    };
    TextField.prototype.clone = function () {
        var newInstance = TextField.getNewTextField();
        this.copyTo(newInstance);
        return newInstance;
    };
    TextField.prototype.copyTo = function (newInstance) {
        _super.prototype.copyTo.call(this, newInstance);
        newInstance.autoSize = this.autoSize;
        newInstance.type = this._type;
        newInstance.html = this.html;
        newInstance.width = this._width;
        newInstance.height = this._height;
        newInstance.newTextFormat = this._newTextFormat.clone();
        newInstance.textColor = this._textColor;
        newInstance.border = this._border;
        newInstance.borderColor = this._borderColor;
        newInstance.background = this._background;
        newInstance.backgroundColor = this._backgroundColor;
        newInstance.textOffsetX = this.textOffsetX;
        newInstance.textOffsetY = this.textOffsetY;
        newInstance.staticMatrix = this.staticMatrix;
        newInstance.selectable = this._selectable;
        newInstance.multiline = this.multiline;
        newInstance.wordWrap = this.wordWrap;
        newInstance.maxChars = this.maxChars;
        newInstance.sourceTextField = this;
        if (newInstance.html) {
            newInstance.htmlText = this.htmlText;
        }
        else {
            newInstance.text = this.text;
        }
        if (this._labelData) {
            newInstance.setLabelData(this._labelData);
        }
    };
    TextField.prototype._clearTextShapes = function () {
        this._textShapesDirty = false;
        this._lastWordsCount = 0;
        if (this.targetGraphics)
            this.targetGraphics.clear();
        var textShape;
        for (var key in this.textShapes) {
            textShape = this.textShapes[key];
            if (textShape.shape) {
                textShape.shape.dispose();
            }
            textShape.shape = null;
            textShape.elements = null;
            textShape.length = 0;
            delete this.textShapes[key];
        }
    };
    TextField._textFields = [];
    TextField.assetType = '[asset TextField]';
    TextField._onChangedEvent = new TextfieldEvent(TextfieldEvent.CHANGED);
    return TextField;
}(DisplayObjectContainer));
export { TextField };
